import Phaser from 'phaser';
import {
    TILE_SIZE,
    CHUNK_SIZE,
    CHUNKS_LOADED,
    MOVE_INTERVAL,
    JUMPSQUAT_DURATION,
    MIN_JUMP_TILES,
    JUMP_HEIGHT,
    PLAYER_HEIGHT_STANDING,
    PLAYER_HEIGHT_CROUCHING,
    PlayerState,
    TileType,
    ChunkData,
    SlashData,
    HP_DEFAULT,
    SWORD_OFFSET,
    SLASH_SPAWN_OFFSET,
    SLASH_DURATION,
    SLASH_RATE,
    TICK_INTERVAL,
    isPassable,
    isBodyPassable,
    isLadder,
    MouseSimulation,
    RodentType,
    SWORD_DESTROY_COOLDOWN_TICKS,
    TILE_DICTIONARY_MAP,
    getRoomBoxes,
    RoomBox,
    calculateSurfaceY,
    calculatePortalSurfaceY,
    UNDERGROUND_LEVEL,
    MAX_AIR_TICKS,
    UNDERGROUND_SPAWN_X,
    UNDERGROUND_SPAWN_Y,
    PLACEHOLDER_TILES,
    isSandTile,
    SPAWN_CENTER_X,
} from '@blockworld/shared';
import { PlayerSnapshot } from '../network/NetworkClient';
import { PositionQueue } from '../network/PositionQueue';
import { GameClient } from '../network/GameClient';
import { TouchControls } from '../input/TouchControls';
import { TextInputScene } from './TextInputScene';
import { DictionaryScene } from './DictionaryScene';
import { GlyphPickerScene } from './GlyphPickerScene';
import { SpriteBrowserScene } from './SpriteBrowserScene';
import { StartMenuScene } from './StartMenuScene';
import { DeathScreenScene } from './DeathScreenScene';
import { PaletteScene } from './PaletteScene';
import { SpeechBubble } from '../ui/SpeechBubble';
import { CombatHUD } from '../ui/CombatHUD';
import { createMicroFont } from '../rendering/PixelFont';
import { TokiPonaAudio, VOICE_PROFILES } from '../toki/TokiPonaAudio';
import { TextureManager } from '../rendering/TextureManager';
import { ChunkManager } from '../world/ChunkManager';
import { SkyboxRenderer } from './game/SkyboxRenderer';
import { DeathAnimationSystem } from './game/DeathAnimationSystem';
import { FallingBlockPredictor } from './game/FallingBlockPredictor';
import { CombatRenderer } from './game/CombatRenderer';
import { setupSceneNetworkCallbacks } from './game/SceneNetworkBridge';
import { handleDownAction, executeDownAction } from './game/WorldInteractionSystem';
import { renderSceneFrame } from './game/SceneRenderSystem';
import {
    updateLocalPlayer,
    processBlockInput,
    processCombatInput,
    processMovementInput,
    processJumpAndFall,
    processGlobalTick,
    processContinuousCombatInput,
    doVerticalMove,
    checkFallOffLedge,
} from './game/PlayerMotionSystem';
import { MentorBehavior } from '../npc/MentorBehavior';
import { LoverBehavior } from '../npc/LoverBehavior';
import { HeroBehavior } from '../npc/HeroBehavior';
import type { ProcessedTextures, PlayerTextures } from '../rendering/TextureManager';
import type { AudioProfile } from '../toki/TokiPonaAudio';

// Performance timing helper (uses baseline from main.ts)
const _perf = (label: string) => {
    const fn = (window as any).__perf;
    if (fn) fn(label); else console.log(`[PERF] ${label}`);
};

// ============================================================
// SPRITE FILE LISTS (data-driven loading)
// ============================================================

// KASI plant sprites — eagerly loaded in preload() (all 120 are small 8x8 PNGs)
const KASI_SPRITE_NAMES: string[] = [
    'kasi_a', 'kasi_akesi', 'kasi_ala', 'kasi_alasa', 'kasi_ale', 'kasi_anpa',
    'kasi_ante', 'kasi_anu', 'kasi_awen', 'kasi_e', 'kasi_en', 'kasi_esun',
    'kasi_ijo', 'kasi_ike', 'kasi_ilo', 'kasi_insa', 'kasi_jaki', 'kasi_jan',
    'kasi_jelo', 'kasi_jo', 'kasi_kala', 'kasi_kalama', 'kasi_kama', 'kasi_kasi',
    'kasi_ken', 'kasi_kepeken', 'kasi_kili', 'kasi_kiwen', 'kasi_ko', 'kasi_kon',
    'kasi_kule', 'kasi_kulupu', 'kasi_kute', 'kasi_la', 'kasi_lape', 'kasi_laso',
    'kasi_lawa', 'kasi_len', 'kasi_lete', 'kasi_li', 'kasi_lili', 'kasi_linja',
    'kasi_lipu', 'kasi_loje', 'kasi_lon', 'kasi_luka', 'kasi_lukin', 'kasi_lupa',
    'kasi_ma', 'kasi_mama', 'kasi_mani', 'kasi_meli', 'kasi_mi', 'kasi_mije',
    'kasi_moku', 'kasi_moli', 'kasi_monsi', 'kasi_mu', 'kasi_mun', 'kasi_musi',
    'kasi_mute', 'kasi_nanpa', 'kasi_nasa', 'kasi_nasin', 'kasi_nena', 'kasi_ni',
    'kasi_nimi', 'kasi_noka', 'kasi_o', 'kasi_olin', 'kasi_ona', 'kasi_open',
    'kasi_pakala', 'kasi_pali', 'kasi_palisa', 'kasi_pan', 'kasi_pana', 'kasi_pi',
    'kasi_pilin', 'kasi_pimeja', 'kasi_pini', 'kasi_pipi', 'kasi_poka', 'kasi_poki',
    'kasi_pona', 'kasi_pu', 'kasi_sama', 'kasi_seli', 'kasi_selo', 'kasi_seme',
    'kasi_sewi', 'kasi_sijelo', 'kasi_sike', 'kasi_sin', 'kasi_sina', 'kasi_sinpin',
    'kasi_sitelen', 'kasi_sona', 'kasi_soweli', 'kasi_suli', 'kasi_suno', 'kasi_supa',
    'kasi_suwi', 'kasi_tan', 'kasi_taso', 'kasi_tawa', 'kasi_telo', 'kasi_tenpo',
    'kasi_toki', 'kasi_tomo', 'kasi_tu', 'kasi_unpa', 'kasi_uta', 'kasi_utala',
    'kasi_walo', 'kasi_wan', 'kasi_waso', 'kasi_wawa', 'kasi_weka', 'kasi_wile',
];

// Tile sprites: ILO (tools) and KIWEN (blocks) that replace color+glyph placeholders
const TILE_SPRITE_FILES: string[] = [
    'ilo_ala', 'ilo_alasa', 'ilo_ale', 'ilo_awen', 'ilo_en', 'ilo_esun',
    'ilo_ilo', 'ilo_jan', 'ilo_jelo', 'ilo_jo', 'ilo_kala', 'ilo_kalama',
    'ilo_kasi', 'ilo_ken', 'ilo_kepeken', 'ilo_kiwen', 'ilo_kon', 'ilo_kule',
    'ilo_kute', 'ilo_la', 'ilo_lape', 'ilo_len', 'ilo_li', 'ilo_lili',
    'ilo_lipu', 'ilo_loje', 'ilo_lukin', 'ilo_ma', 'ilo_mama', 'ilo_mani',
    'ilo_mije', 'ilo_moku', 'ilo_moli', 'ilo_mu', 'ilo_mun', 'ilo_nasin',
    'ilo_nimi', 'ilo_o', 'ilo_olin', 'ilo_open', 'ilo_palisa', 'ilo_pan',
    'ilo_pilin', 'ilo_pipi', 'ilo_poka', 'ilo_seli', 'ilo_selo', 'ilo_seme',
    'ilo_sike', 'ilo_sitelen', 'ilo_soweli', 'ilo_suno', 'ilo_tenpo', 'ilo_toki',
    'ilo_tu', 'ilo_uta', 'ilo_utala', 'ilo_wan',
    // kiwen (blocks) — 98 placeholder sprites replacing blue-grey squares
    'kiwen_a', 'kiwen_akesi', 'kiwen_ala', 'kiwen_alasa', 'kiwen_ale',
    'kiwen_ante', 'kiwen_anu', 'kiwen_awen', 'kiwen_e', 'kiwen_en',
    'kiwen_esun', 'kiwen_ijo', 'kiwen_ike', 'kiwen_jaki', 'kiwen_jan',
    'kiwen_jelo', 'kiwen_jo', 'kiwen_kala', 'kiwen_kalama', 'kiwen_kama',
    'kiwen_ken', 'kiwen_kepeken', 'kiwen_kili', 'kiwen_ko', 'kiwen_kon',
    'kiwen_kulupu', 'kiwen_kute', 'kiwen_lape', 'kiwen_len', 'kiwen_lete',
    'kiwen_li', 'kiwen_lili', 'kiwen_linja', 'kiwen_lipu', 'kiwen_loje',
    'kiwen_lon', 'kiwen_luka', 'kiwen_mama', 'kiwen_mani', 'kiwen_meli',
    'kiwen_mije', 'kiwen_moku', 'kiwen_moli', 'kiwen_monsi', 'kiwen_mu',
    'kiwen_mun', 'kiwen_musi', 'kiwen_mute', 'kiwen_nanpa', 'kiwen_nasa',
    'kiwen_nasin', 'kiwen_nena', 'kiwen_ni', 'kiwen_o', 'kiwen_olin',
    'kiwen_ona', 'kiwen_pakala', 'kiwen_pali', 'kiwen_palisa', 'kiwen_pan',
    'kiwen_pana', 'kiwen_pi', 'kiwen_pimeja', 'kiwen_pini', 'kiwen_pipi',
    'kiwen_poka', 'kiwen_poki', 'kiwen_pona', 'kiwen_pu', 'kiwen_sama',
    'kiwen_seli', 'kiwen_selo', 'kiwen_sijelo', 'kiwen_sike', 'kiwen_sin',
    'kiwen_sina', 'kiwen_sinpin', 'kiwen_sitelen', 'kiwen_sona', 'kiwen_suli',
    'kiwen_suno', 'kiwen_suwi', 'kiwen_tan', 'kiwen_taso', 'kiwen_tawa',
    'kiwen_telo', 'kiwen_tenpo', 'kiwen_tomo', 'kiwen_tu', 'kiwen_unpa',
    'kiwen_uta', 'kiwen_utala', 'kiwen_walo', 'kiwen_wan', 'kiwen_waso',
    'kiwen_wawa', 'kiwen_weka', 'kiwen_wile',
    // kili (fruits) — 36 original fruit sprites
    'kili_akesi', 'kili_ale', 'kili_anpa', 'kili_esun', 'kili_ijo', 'kili_ike',
    'kili_insa', 'kili_jaki', 'kili_jelo', 'kili_ken', 'kili_kili', 'kili_kiwen',
    'kili_kulupu', 'kili_laso', 'kili_lawa', 'kili_lete', 'kili_linja', 'kili_loje',
    'kili_ma', 'kili_moli', 'kili_musi', 'kili_mute', 'kili_nanpa', 'kili_open',
    'kili_pakala', 'kili_palisa', 'kili_pan', 'kili_pu', 'kili_seli', 'kili_selo',
    'kili_sona', 'kili_soweli', 'kili_suwi', 'kili_telo', 'kili_unpa', 'kili_wile',
    // kili (fruits) — 84 expanded fruit sprites
    'kili_a', 'kili_ala', 'kili_alasa', 'kili_ante', 'kili_anu', 'kili_awen',
    'kili_e', 'kili_en', 'kili_ilo', 'kili_jan', 'kili_jo', 'kili_kala',
    'kili_kalama', 'kili_kama', 'kili_kasi', 'kili_kepeken', 'kili_ko', 'kili_kon',
    'kili_kule', 'kili_kute', 'kili_la', 'kili_lape', 'kili_len', 'kili_li',
    'kili_lili', 'kili_lipu', 'kili_lon', 'kili_luka', 'kili_lukin', 'kili_lupa',
    'kili_mama', 'kili_mani', 'kili_meli', 'kili_mi', 'kili_mije', 'kili_moku',
    'kili_monsi', 'kili_mu', 'kili_mun', 'kili_nasa', 'kili_nasin', 'kili_nena',
    'kili_ni', 'kili_nimi', 'kili_noka', 'kili_o', 'kili_olin', 'kili_ona',
    'kili_pali', 'kili_pana', 'kili_pi', 'kili_pilin', 'kili_pimeja', 'kili_pini',
    'kili_pipi', 'kili_poka', 'kili_poki', 'kili_pona', 'kili_sama', 'kili_seme',
    'kili_sewi', 'kili_sijelo', 'kili_sike', 'kili_sin', 'kili_sina', 'kili_sinpin',
    'kili_sitelen', 'kili_suli', 'kili_suno', 'kili_supa', 'kili_tan', 'kili_taso',
    'kili_tawa', 'kili_tenpo', 'kili_toki', 'kili_tomo', 'kili_tu', 'kili_uta',
    'kili_utala', 'kili_walo', 'kili_wan', 'kili_waso', 'kili_wawa', 'kili_weka',
];

// Creature sprites: [spriteFilename, creatureId]
// creatureId matches the rodent type string used in SceneRenderSystem (e.g. "soweli_2")
const CREATURE_SPRITE_FILES: [string, string][] = [
    ['akesi_a', 'akesi_0'], ['akesi_akesi', 'akesi_1'], ['akesi_alasa', 'akesi_3'],
    ['akesi_anpa', 'akesi_5'], ['akesi_ante', 'akesi_6'], ['akesi_awen', 'akesi_8'],
    ['akesi_e', 'akesi_9'], ['akesi_en', 'akesi_10'], ['akesi_ijo', 'akesi_12'],
    ['akesi_jaki', 'akesi_16'], ['akesi_jan', 'akesi_17'], ['akesi_jelo', 'akesi_18'],
    ['akesi_kala', 'akesi_20'], ['akesi_kalama', 'akesi_21'], ['akesi_kasi', 'akesi_23'],
    ['akesi_ken', 'akesi_24'], ['akesi_kili', 'akesi_26'], ['akesi_kiwen', 'akesi_27'],
    ['akesi_ko', 'akesi_28'], ['akesi_kon', 'akesi_29'], ['akesi_kule', 'akesi_30'],
    ['akesi_kulupu', 'akesi_31'], ['akesi_lawa', 'akesi_36'], ['akesi_loje', 'akesi_43'],
    ['akesi_mani', 'akesi_50'], ['akesi_moku', 'akesi_54'], ['akesi_mun', 'akesi_58'],
    ['akesi_mute', 'akesi_60'], ['akesi_palisa', 'akesi_74'], ['akesi_pan', 'akesi_75'],
    ['akesi_pana', 'akesi_76'], ['akesi_pimeja', 'akesi_79'], ['akesi_pipi', 'akesi_81'],
    ['akesi_poki', 'akesi_83'], ['akesi_pona', 'akesi_84'], ['akesi_seli', 'akesi_87'],
    ['akesi_selo', 'akesi_88'], ['akesi_sewi', 'akesi_90'], ['akesi_soweli', 'akesi_98'], ['akesi_suli', 'akesi_99'],
    ['akesi_suwi', 'akesi_102'], ['akesi_telo', 'akesi_106'], ['akesi_tomo', 'akesi_109'],
    ['akesi_uta', 'akesi_112'], ['akesi_wile', 'akesi_119'],
    ['kala_ala', 'kala_2'], ['kala_alasa', 'kala_3'],
    ['kala_akesi', 'kala_1'], ['kala_anpa', 'kala_5'], ['kala_ante', 'kala_6'],
    ['kala_e', 'kala_9'], ['kala_en', 'kala_10'], ['kala_ijo', 'kala_12'],
    ['kala_ike', 'kala_13'], ['kala_insa', 'kala_15'], ['kala_jan', 'kala_17'],
    ['kala_jo', 'kala_19'],
    ['kala_kalama', 'kala_21'], ['kala_kasi', 'kala_23'], ['kala_kepeken', 'kala_25'],
    ['kala_ko', 'kala_28'], ['kala_kon', 'kala_29'], ['kala_kulupu', 'kala_31'],
    ['kala_lape', 'kala_34'], ['kala_lili', 'kala_40'], ['kala_linja', 'kala_41'],
    ['kala_lipu', 'kala_42'],
    ['kala_luka', 'kala_45'], ['kala_lupa', 'kala_47'], ['kala_ma', 'kala_48'],
    ['kala_mi', 'kala_52'], ['kala_mije', 'kala_53'],
    ['kala_mun', 'kala_58'], ['kala_musi', 'kala_59'], ['kala_nasa', 'kala_62'],
    ['kala_nena', 'kala_64'], ['kala_ni', 'kala_65'],
    ['kala_pakala', 'kala_72'], ['kala_pali', 'kala_73'],
    ['kala_palisa', 'kala_74'], ['kala_poka', 'kala_82'], ['kala_poki', 'kala_83'],
    ['kala_sewi', 'kala_90'], ['kala_sitelen', 'kala_96'], ['kala_soweli', 'kala_98'],
    ['kala_suli', 'kala_99'], ['kala_suno', 'kala_100'], ['kala_telo', 'kala_106'],
    ['kala_toki', 'kala_108'], ['kala_tomo', 'kala_109'], ['kala_uta', 'kala_112'],
    ['kala_utala', 'kala_113'], ['kala_waso', 'kala_116'], ['kala_wawa', 'kala_117'],
    ['soweli_ala', 'soweli_2'], ['soweli_ale', 'soweli_4'], ['soweli_e', 'soweli_9'],
    ['soweli_ike', 'soweli_13'], ['soweli_ilo', 'soweli_14'],
    ['soweli_jaki', 'soweli_16'], ['soweli_jan', 'soweli_17'], ['soweli_jelo', 'soweli_18'],
    ['soweli_kala', 'soweli_20'], ['soweli_kalama', 'soweli_21'], ['soweli_kama', 'soweli_22'],
    ['soweli_kepeken', 'soweli_25'], ['soweli_kili', 'soweli_26'], ['soweli_kiwen', 'soweli_27'],
    ['soweli_kon', 'soweli_29'],
    ['soweli_kulupu', 'soweli_31'], ['soweli_lape', 'soweli_34'], ['soweli_lete', 'soweli_38'],
    ['soweli_linja', 'soweli_41'],
    ['soweli_loje', 'soweli_43'], ['soweli_mama', 'soweli_49'], ['soweli_mani', 'soweli_50'],
    ['soweli_mi', 'soweli_52'],
    ['soweli_moku', 'soweli_54'], ['soweli_mu', 'soweli_57'], ['soweli_mute', 'soweli_60'], ['soweli_nanpa', 'soweli_61'],
    ['soweli_nasa', 'soweli_62'], ['soweli_nena', 'soweli_64'], ['soweli_nimi', 'soweli_66'], ['soweli_noka', 'soweli_67'],
    ['soweli_o', 'soweli_68'], ['soweli_olin', 'soweli_69'], ['soweli_pakala', 'soweli_72'],
    ['soweli_pali', 'soweli_73'], ['soweli_pan', 'soweli_75'], ['soweli_pimeja', 'soweli_79'], ['soweli_poka', 'soweli_82'],
    ['soweli_poki', 'soweli_83'], ['soweli_pona', 'soweli_84'], ['soweli_pu', 'soweli_85'],
    ['soweli_seli', 'soweli_87'], ['soweli_seme', 'soweli_89'], ['soweli_sewi', 'soweli_90'],
    ['soweli_sitelen', 'soweli_96'], ['soweli_soweli', 'soweli_98'], ['soweli_suli', 'soweli_99'],
    ['soweli_telo', 'soweli_106'], ['soweli_tenpo', 'soweli_107'], ['soweli_toki', 'soweli_108'],
    ['soweli_tomo', 'soweli_109'], ['soweli_uta', 'soweli_112'], ['soweli_utala', 'soweli_113'],
    ['soweli_waso', 'soweli_116'], ['soweli_wawa', 'soweli_117'], ['soweli_weka', 'soweli_118'],
    ['waso_alasa', 'waso_3'], ['waso_akesi', 'waso_1'], ['waso_esun', 'waso_11'], ['waso_ijo', 'waso_12'],
    ['waso_ilo', 'waso_14'], ['waso_jan', 'waso_17'], ['waso_jelo', 'waso_18'], ['waso_jo', 'waso_19'],
    ['waso_kili', 'waso_26'],
    ['waso_kiwen', 'waso_27'], ['waso_kute', 'waso_32'], ['waso_lete', 'waso_38'],
    ['waso_lili', 'waso_40'], ['waso_linja', 'waso_41'], ['waso_lukin', 'waso_46'],
    ['waso_lupa', 'waso_47'], ['waso_mama', 'waso_49'], ['waso_mani', 'waso_50'],
    ['waso_meli', 'waso_51'], ['waso_moku', 'waso_54'], ['waso_mun', 'waso_58'],
    ['waso_musi', 'waso_59'], ['waso_nasa', 'waso_62'], ['waso_nena', 'waso_64'], ['waso_pakala', 'waso_72'],
    ['waso_pali', 'waso_73'], ['waso_pimeja', 'waso_79'], ['waso_pini', 'waso_80'],
    ['waso_pipi', 'waso_81'], ['waso_pona', 'waso_84'], ['waso_seli', 'waso_87'],
    ['waso_sona', 'waso_97'], ['waso_soweli', 'waso_98'], ['waso_suli', 'waso_99'],
    ['waso_suno', 'waso_100'], ['waso_tawa', 'waso_105'], ['waso_telo', 'waso_106'],
    ['waso_tenpo', 'waso_107'], ['waso_toki', 'waso_108'], ['waso_uta', 'waso_112'],
    ['waso_wawa', 'waso_117'], ['waso_wile', 'waso_119'],
    // Grammar-word creatures (soweli/akesi/kala/waso + grammar particles)
    ['soweli_anu', 'soweli_7'], ['soweli_la', 'soweli_33'], ['soweli_li', 'soweli_39'],
    ['soweli_ni', 'soweli_65'], ['soweli_ona', 'soweli_70'], ['soweli_pi', 'soweli_77'],
    ['soweli_sama', 'soweli_86'], ['soweli_sina', 'soweli_94'], ['soweli_taso', 'soweli_104'],
    ['akesi_anu', 'akesi_7'], ['akesi_la', 'akesi_33'], ['akesi_li', 'akesi_39'],
    ['akesi_mi', 'akesi_52'], ['akesi_ni', 'akesi_65'], ['akesi_o', 'akesi_68'],
    ['akesi_ona', 'akesi_70'], ['akesi_pi', 'akesi_77'], ['akesi_sama', 'akesi_86'],
    ['akesi_sina', 'akesi_94'], ['akesi_taso', 'akesi_104'],
    ['kala_a', 'kala_0'], ['kala_anu', 'kala_7'], ['kala_la', 'kala_33'],
    ['kala_li', 'kala_39'], ['kala_o', 'kala_68'], ['kala_ona', 'kala_70'],
    ['kala_pi', 'kala_77'], ['kala_sama', 'kala_86'], ['kala_sina', 'kala_94'],
    ['kala_taso', 'kala_104'],
    ['waso_a', 'waso_0'], ['waso_anu', 'waso_7'], ['waso_e', 'waso_9'],
    ['waso_la', 'waso_33'], ['waso_li', 'waso_39'], ['waso_mi', 'waso_52'],
    ['waso_ni', 'waso_65'], ['waso_o', 'waso_68'], ['waso_ona', 'waso_70'],
    ['waso_pi', 'waso_77'], ['waso_sama', 'waso_86'], ['waso_sina', 'waso_94'],
    ['waso_taso', 'waso_104'],
    // Generated sprites for remaining creatures (245 entries)
    ['soweli_a', 'soweli_0'], ['soweli_akesi', 'soweli_1'], ['soweli_alasa', 'soweli_3'],
    ['soweli_anpa', 'soweli_5'], ['soweli_ante', 'soweli_6'], ['soweli_awen', 'soweli_8'],
    ['soweli_en', 'soweli_10'], ['soweli_esun', 'soweli_11'], ['soweli_ijo', 'soweli_12'],
    ['soweli_insa', 'soweli_15'], ['soweli_jo', 'soweli_19'], ['soweli_kasi', 'soweli_23'],
    ['soweli_ken', 'soweli_24'], ['soweli_ko', 'soweli_28'], ['soweli_kule', 'soweli_30'],
    ['soweli_kute', 'soweli_32'], ['soweli_laso', 'soweli_35'], ['soweli_lawa', 'soweli_36'],
    ['soweli_len', 'soweli_37'], ['soweli_lili', 'soweli_40'], ['soweli_lipu', 'soweli_42'],
    ['soweli_lon', 'soweli_44'], ['soweli_luka', 'soweli_45'], ['soweli_lukin', 'soweli_46'],
    ['soweli_lupa', 'soweli_47'], ['soweli_ma', 'soweli_48'], ['soweli_meli', 'soweli_51'],
    ['soweli_mije', 'soweli_53'], ['soweli_moli', 'soweli_55'], ['soweli_monsi', 'soweli_56'],
    ['soweli_mun', 'soweli_58'], ['soweli_musi', 'soweli_59'], ['soweli_nasin', 'soweli_63'],
    ['soweli_open', 'soweli_71'], ['soweli_palisa', 'soweli_74'], ['soweli_pana', 'soweli_76'],
    ['soweli_pilin', 'soweli_78'], ['soweli_pini', 'soweli_80'], ['soweli_pipi', 'soweli_81'],
    ['soweli_selo', 'soweli_88'], ['soweli_sijelo', 'soweli_91'], ['soweli_sike', 'soweli_92'],
    ['soweli_sin', 'soweli_93'], ['soweli_sinpin', 'soweli_95'], ['soweli_sona', 'soweli_97'],
    ['soweli_suno', 'soweli_100'], ['soweli_supa', 'soweli_101'], ['soweli_suwi', 'soweli_102'],
    ['soweli_tan', 'soweli_103'], ['soweli_tawa', 'soweli_105'], ['soweli_tu', 'soweli_110'],
    ['soweli_unpa', 'soweli_111'], ['soweli_walo', 'soweli_114'], ['soweli_wan', 'soweli_115'],
    ['soweli_wile', 'soweli_119'],
    ['akesi_ala', 'akesi_2'], ['akesi_ale', 'akesi_4'], ['akesi_esun', 'akesi_11'],
    ['akesi_ike', 'akesi_13'], ['akesi_ilo', 'akesi_14'], ['akesi_insa', 'akesi_15'],
    ['akesi_jo', 'akesi_19'], ['akesi_kama', 'akesi_22'], ['akesi_kepeken', 'akesi_25'],
    ['akesi_kute', 'akesi_32'], ['akesi_lape', 'akesi_34'], ['akesi_laso', 'akesi_35'],
    ['akesi_len', 'akesi_37'], ['akesi_lete', 'akesi_38'], ['akesi_lili', 'akesi_40'],
    ['akesi_linja', 'akesi_41'], ['akesi_lipu', 'akesi_42'], ['akesi_lon', 'akesi_44'],
    ['akesi_luka', 'akesi_45'], ['akesi_lukin', 'akesi_46'], ['akesi_lupa', 'akesi_47'],
    ['akesi_ma', 'akesi_48'], ['akesi_mama', 'akesi_49'], ['akesi_meli', 'akesi_51'],
    ['akesi_mije', 'akesi_53'], ['akesi_moli', 'akesi_55'], ['akesi_monsi', 'akesi_56'],
    ['akesi_mu', 'akesi_57'], ['akesi_musi', 'akesi_59'], ['akesi_nanpa', 'akesi_61'],
    ['akesi_nasa', 'akesi_62'], ['akesi_nasin', 'akesi_63'], ['akesi_nena', 'akesi_64'],
    ['akesi_nimi', 'akesi_66'], ['akesi_noka', 'akesi_67'], ['akesi_olin', 'akesi_69'],
    ['akesi_open', 'akesi_71'], ['akesi_pakala', 'akesi_72'], ['akesi_pali', 'akesi_73'],
    ['akesi_pilin', 'akesi_78'], ['akesi_pini', 'akesi_80'], ['akesi_poka', 'akesi_82'],
    ['akesi_pu', 'akesi_85'], ['akesi_seme', 'akesi_89'], ['akesi_sijelo', 'akesi_91'],
    ['akesi_sike', 'akesi_92'], ['akesi_sin', 'akesi_93'], ['akesi_sinpin', 'akesi_95'],
    ['akesi_sitelen', 'akesi_96'], ['akesi_sona', 'akesi_97'], ['akesi_suno', 'akesi_100'],
    ['akesi_supa', 'akesi_101'], ['akesi_tan', 'akesi_103'], ['akesi_tawa', 'akesi_105'],
    ['akesi_tenpo', 'akesi_107'], ['akesi_toki', 'akesi_108'], ['akesi_tu', 'akesi_110'],
    ['akesi_unpa', 'akesi_111'], ['akesi_utala', 'akesi_113'], ['akesi_walo', 'akesi_114'],
    ['akesi_wan', 'akesi_115'], ['akesi_waso', 'akesi_116'], ['akesi_wawa', 'akesi_117'],
    ['akesi_weka', 'akesi_118'],
    ['kala_ale', 'kala_4'], ['kala_awen', 'kala_8'], ['kala_esun', 'kala_11'],
    ['kala_ilo', 'kala_14'], ['kala_jaki', 'kala_16'], ['kala_jelo', 'kala_18'],
    ['kala_kala', 'kala_20'], ['kala_kama', 'kala_22'], ['kala_ken', 'kala_24'],
    ['kala_kili', 'kala_26'], ['kala_kiwen', 'kala_27'], ['kala_kule', 'kala_30'],
    ['kala_kute', 'kala_32'], ['kala_laso', 'kala_35'], ['kala_lawa', 'kala_36'],
    ['kala_len', 'kala_37'], ['kala_lete', 'kala_38'], ['kala_loje', 'kala_43'],
    ['kala_lon', 'kala_44'], ['kala_lukin', 'kala_46'], ['kala_mama', 'kala_49'],
    ['kala_mani', 'kala_50'], ['kala_meli', 'kala_51'], ['kala_moku', 'kala_54'],
    ['kala_moli', 'kala_55'], ['kala_monsi', 'kala_56'], ['kala_mu', 'kala_57'],
    ['kala_mute', 'kala_60'], ['kala_nanpa', 'kala_61'], ['kala_nasin', 'kala_63'],
    ['kala_nimi', 'kala_66'], ['kala_noka', 'kala_67'], ['kala_olin', 'kala_69'],
    ['kala_open', 'kala_71'], ['kala_pan', 'kala_75'], ['kala_pana', 'kala_76'],
    ['kala_pilin', 'kala_78'], ['kala_pimeja', 'kala_79'], ['kala_pini', 'kala_80'],
    ['kala_pipi', 'kala_81'], ['kala_pona', 'kala_84'], ['kala_pu', 'kala_85'],
    ['kala_seli', 'kala_87'], ['kala_selo', 'kala_88'], ['kala_seme', 'kala_89'],
    ['kala_sijelo', 'kala_91'], ['kala_sike', 'kala_92'], ['kala_sin', 'kala_93'],
    ['kala_sinpin', 'kala_95'], ['kala_sona', 'kala_97'], ['kala_supa', 'kala_101'],
    ['kala_suwi', 'kala_102'], ['kala_tan', 'kala_103'], ['kala_tawa', 'kala_105'],
    ['kala_tenpo', 'kala_107'], ['kala_tu', 'kala_110'], ['kala_unpa', 'kala_111'],
    ['kala_walo', 'kala_114'], ['kala_wan', 'kala_115'], ['kala_weka', 'kala_118'],
    ['kala_wile', 'kala_119'],
    ['waso_ala', 'waso_2'], ['waso_ale', 'waso_4'], ['waso_anpa', 'waso_5'],
    ['waso_ante', 'waso_6'], ['waso_awen', 'waso_8'], ['waso_en', 'waso_10'],
    ['waso_ike', 'waso_13'], ['waso_insa', 'waso_15'], ['waso_jaki', 'waso_16'],
    ['waso_kala', 'waso_20'], ['waso_kalama', 'waso_21'], ['waso_kama', 'waso_22'],
    ['waso_kasi', 'waso_23'], ['waso_ken', 'waso_24'], ['waso_kepeken', 'waso_25'],
    ['waso_ko', 'waso_28'], ['waso_kon', 'waso_29'], ['waso_kule', 'waso_30'],
    ['waso_kulupu', 'waso_31'], ['waso_lape', 'waso_34'], ['waso_laso', 'waso_35'],
    ['waso_lawa', 'waso_36'], ['waso_len', 'waso_37'], ['waso_lipu', 'waso_42'],
    ['waso_loje', 'waso_43'], ['waso_lon', 'waso_44'], ['waso_luka', 'waso_45'],
    ['waso_ma', 'waso_48'], ['waso_mije', 'waso_53'], ['waso_moli', 'waso_55'],
    ['waso_monsi', 'waso_56'], ['waso_mu', 'waso_57'], ['waso_mute', 'waso_60'],
    ['waso_nanpa', 'waso_61'], ['waso_nasin', 'waso_63'], ['waso_nimi', 'waso_66'],
    ['waso_noka', 'waso_67'], ['waso_olin', 'waso_69'], ['waso_open', 'waso_71'],
    ['waso_palisa', 'waso_74'], ['waso_pan', 'waso_75'], ['waso_pana', 'waso_76'],
    ['waso_pilin', 'waso_78'], ['waso_poka', 'waso_82'], ['waso_poki', 'waso_83'],
    ['waso_pu', 'waso_85'], ['waso_selo', 'waso_88'], ['waso_seme', 'waso_89'],
    ['waso_sewi', 'waso_90'], ['waso_sijelo', 'waso_91'], ['waso_sike', 'waso_92'],
    ['waso_sin', 'waso_93'], ['waso_sinpin', 'waso_95'], ['waso_sitelen', 'waso_96'],
    ['waso_supa', 'waso_101'], ['waso_suwi', 'waso_102'], ['waso_tan', 'waso_103'],
    ['waso_tomo', 'waso_109'], ['waso_tu', 'waso_110'], ['waso_unpa', 'waso_111'],
    ['waso_utala', 'waso_113'], ['waso_walo', 'waso_114'], ['waso_wan', 'waso_115'],
    ['waso_waso', 'waso_116'], ['waso_weka', 'waso_118'],
    // Baby sprites for multi-tile creatures (30 entries)
    ['soweli_pona_baby', 'soweli_84_baby'], ['soweli_nimi_baby', 'soweli_66_baby'],
    ['soweli_sewi_baby', 'soweli_90_baby'], ['soweli_suli_baby', 'soweli_99_baby'],
    ['soweli_mi_baby', 'soweli_52_baby'], ['soweli_anu_baby', 'soweli_7_baby'],
    ['soweli_sama_baby', 'soweli_86_baby'], ['soweli_sina_baby', 'soweli_94_baby'],
    ['soweli_taso_baby', 'soweli_104_baby'], ['soweli_la_baby', 'soweli_33_baby'],
    ['soweli_ni_baby', 'soweli_65_baby'], ['soweli_ona_baby', 'soweli_70_baby'],
    ['akesi_li_baby', 'akesi_39_baby'], ['akesi_sama_baby', 'akesi_86_baby'],
    ['akesi_sina_baby', 'akesi_94_baby'], ['akesi_la_baby', 'akesi_33_baby'],
    ['akesi_ona_baby', 'akesi_70_baby'],
    ['kala_a_baby', 'kala_0_baby'], ['kala_anu_baby', 'kala_7_baby'],
    ['kala_sina_baby', 'kala_94_baby'], ['kala_taso_baby', 'kala_104_baby'],
    ['kala_la_baby', 'kala_33_baby'], ['kala_ona_baby', 'kala_70_baby'],
    ['kala_suli_baby', 'kala_99_baby'], ['kala_alasa_baby', 'kala_3_baby'],
    ['kala_lipu_baby', 'kala_42_baby'], ['kala_toki_baby', 'kala_108_baby'],
    ['waso_a_baby', 'waso_0_baby'], ['waso_ni_baby', 'waso_65_baby'],
    ['waso_alasa_baby', 'waso_3_baby'],
    // Pipi (insects) — 120 creature sprites with cling AI
    ['pipi_a', 'pipi_0'], ['pipi_akesi', 'pipi_1'], ['pipi_ala', 'pipi_2'], ['pipi_alasa', 'pipi_3'],
    ['pipi_ale', 'pipi_4'], ['pipi_anpa', 'pipi_5'], ['pipi_ante', 'pipi_6'], ['pipi_anu', 'pipi_7'],
    ['pipi_awen', 'pipi_8'], ['pipi_e', 'pipi_9'], ['pipi_en', 'pipi_10'], ['pipi_esun', 'pipi_11'],
    ['pipi_ijo', 'pipi_12'], ['pipi_ike', 'pipi_13'], ['pipi_ilo', 'pipi_14'], ['pipi_insa', 'pipi_15'],
    ['pipi_jaki', 'pipi_16'], ['pipi_jan', 'pipi_17'], ['pipi_jelo', 'pipi_18'], ['pipi_jo', 'pipi_19'],
    ['pipi_kala', 'pipi_20'], ['pipi_kalama', 'pipi_21'], ['pipi_kama', 'pipi_22'], ['pipi_kasi', 'pipi_23'],
    ['pipi_ken', 'pipi_24'], ['pipi_kepeken', 'pipi_25'], ['pipi_kili', 'pipi_26'], ['pipi_kiwen', 'pipi_27'],
    ['pipi_ko', 'pipi_28'], ['pipi_kon', 'pipi_29'], ['pipi_kule', 'pipi_30'], ['pipi_kulupu', 'pipi_31'],
    ['pipi_kute', 'pipi_32'], ['pipi_la', 'pipi_33'], ['pipi_lape', 'pipi_34'], ['pipi_laso', 'pipi_35'],
    ['pipi_lawa', 'pipi_36'], ['pipi_len', 'pipi_37'], ['pipi_lete', 'pipi_38'], ['pipi_li', 'pipi_39'],
    ['pipi_lili', 'pipi_40'], ['pipi_linja', 'pipi_41'], ['pipi_lipu', 'pipi_42'], ['pipi_loje', 'pipi_43'],
    ['pipi_lon', 'pipi_44'], ['pipi_luka', 'pipi_45'], ['pipi_lukin', 'pipi_46'], ['pipi_lupa', 'pipi_47'],
    ['pipi_ma', 'pipi_48'], ['pipi_mama', 'pipi_49'], ['pipi_mani', 'pipi_50'], ['pipi_meli', 'pipi_51'],
    ['pipi_mi', 'pipi_52'], ['pipi_mije', 'pipi_53'], ['pipi_moku', 'pipi_54'], ['pipi_moli', 'pipi_55'],
    ['pipi_monsi', 'pipi_56'], ['pipi_mu', 'pipi_57'], ['pipi_mun', 'pipi_58'], ['pipi_musi', 'pipi_59'],
    ['pipi_mute', 'pipi_60'], ['pipi_nanpa', 'pipi_61'], ['pipi_nasa', 'pipi_62'], ['pipi_nasin', 'pipi_63'],
    ['pipi_nena', 'pipi_64'], ['pipi_ni', 'pipi_65'], ['pipi_nimi', 'pipi_66'], ['pipi_noka', 'pipi_67'],
    ['pipi_o', 'pipi_68'], ['pipi_olin', 'pipi_69'], ['pipi_ona', 'pipi_70'], ['pipi_open', 'pipi_71'],
    ['pipi_pakala', 'pipi_72'], ['pipi_pali', 'pipi_73'], ['pipi_palisa', 'pipi_74'], ['pipi_pan', 'pipi_75'],
    ['pipi_pana', 'pipi_76'], ['pipi_pi', 'pipi_77'], ['pipi_pilin', 'pipi_78'], ['pipi_pimeja', 'pipi_79'],
    ['pipi_pini', 'pipi_80'], ['pipi_pipi', 'pipi_81'], ['pipi_poka', 'pipi_82'], ['pipi_poki', 'pipi_83'],
    ['pipi_pona', 'pipi_84'], ['pipi_pu', 'pipi_85'], ['pipi_sama', 'pipi_86'], ['pipi_seli', 'pipi_87'],
    ['pipi_selo', 'pipi_88'], ['pipi_seme', 'pipi_89'], ['pipi_sewi', 'pipi_90'], ['pipi_sijelo', 'pipi_91'],
    ['pipi_sike', 'pipi_92'], ['pipi_sin', 'pipi_93'], ['pipi_sina', 'pipi_94'], ['pipi_sinpin', 'pipi_95'],
    ['pipi_sitelen', 'pipi_96'], ['pipi_sona', 'pipi_97'], ['pipi_soweli', 'pipi_98'], ['pipi_suli', 'pipi_99'],
    ['pipi_suno', 'pipi_100'], ['pipi_supa', 'pipi_101'], ['pipi_suwi', 'pipi_102'], ['pipi_tan', 'pipi_103'],
    ['pipi_taso', 'pipi_104'], ['pipi_tawa', 'pipi_105'], ['pipi_telo', 'pipi_106'], ['pipi_tenpo', 'pipi_107'],
    ['pipi_toki', 'pipi_108'], ['pipi_tomo', 'pipi_109'], ['pipi_tu', 'pipi_110'], ['pipi_unpa', 'pipi_111'],
    ['pipi_uta', 'pipi_112'], ['pipi_utala', 'pipi_113'], ['pipi_walo', 'pipi_114'], ['pipi_wan', 'pipi_115'],
    ['pipi_waso', 'pipi_116'], ['pipi_wawa', 'pipi_117'], ['pipi_weka', 'pipi_118'], ['pipi_wile', 'pipi_119'],
];

// ============================================================
// LOCAL PLAYER STATE
// ============================================================

interface LocalPlayerState {
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: TileType | null;
    jumpRemaining: number;
    minJumpRemaining: number;
    jumpsquatStartTime: number;
    risingStartTime: number; // When RISING state started (for stuck detection)
    lastRisingY: number; // Last Y during RISING (to detect stuck)
    pendingLedgeDir: number; // -1, 0, or 1 - direction queued for walk-off-ledge
    pendingBlockPlace: boolean; // true if block place queued for next global tick
    pendingBlockPickup: boolean; // true if block pickup queued for next global tick
    pendingAttackBlock: boolean; // true if spacebar/A block action queued for next global tick
    // Captured coordinates at input time (for falling actions)
    pendingActionX: number;
    pendingActionY: number;
    // Combat
    hp: number;
    maxHp: number;
    isHoldingSword: boolean;
    swordHoldStartTick: number; // Global tick when sword hold started (for slash rate)
    lastSlashSpawnTick: number; // Last tick we spawned a slash (prevents double-spawn)
    lastSwordDestroyTick: number; // Last tick sword destroyed a tile (3-tick cooldown)
    pendingSlash: boolean; // true if slash queued for next tick (while walking)
    // Jump height (modifiable via speech commands)
    jumpHeight: number;
    // Breathing (client-side tracking for bubble display)
    airTicks: number;
    // Equipment slot (A-button action item)
    equippedItem: TileType | null;
    // Visual: player has picked up boots
    hasBoots: boolean;
    // Tool state
    bucketFull: boolean;
    basketContents: number[];
    isGliding: boolean;
    glideDir: number;
    // Custom respawn (KIWEN_MI)
    spawnBlockX: number;
    spawnBlockY: number;
    // Thornbush damage
    thornbushTickCounter: number;
}

// ============================================================
// GAME SCENE
// ============================================================

export class GameScene extends Phaser.Scene {
    private networkClient: GameClient;
    private touchControls!: TouchControls;

    // Skybox renderer (constellation background, shooting star, twinkle, glimmer, room boxes, music)
    private skybox!: SkyboxRenderer;

    // Room boxes — bounding boxes for spawn platform (shared with portal overlay)
    private roomBoxes: RoomBox[] = [];
    // Tracks whether the player has entered the mentor's house (first room box).
    // Once true, respawns go to the mentor's house instead of the underground cavern.
    private hasVisitedMentorHouse = false;

    // World data
    private chunks: Map<string, number[][]> = new Map();
    private worldSeed: number = 0;

    // Server load indicator (0=healthy, 1=degraded, 2=critical)
    serverLoadLevel: number = 0;

    // Sprites
    private localPlayerSprite!: Phaser.GameObjects.Sprite;
    private localHeldBlockSprite!: Phaser.GameObjects.Image;
    private otherPlayerSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
    private otherPlayerHeldBlocks: Map<string, Phaser.GameObjects.Image> = new Map();
    private portalIndicator: Phaser.GameObjects.Image | null = null;
    private standingOnRune: boolean = false;

    // Texture processing (hue shifting, transparency, glyph processing)
    private textureManager!: TextureManager;
    private get processedTextures(): ProcessedTextures { return this.textureManager.processedTextures; }
    private kasiProcessedTextures: Map<TileType, string> = new Map();

    // Tile sprites pool (keyed by screen position for reuse)
    private tileSprites: Map<string, Phaser.GameObjects.Image> = new Map();
    private activeTileKeys: Set<string> = new Set();

    // Input
    private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
    private spaceKey!: Phaser.Input.Keyboard.Key;
    private inputState = {
        left: false,
        right: false,
        up: false,
        down: false,
        downJustPressed: false,
        attack: false,
        attackJustPressed: false,
    };
    private lastDownState = false;
    private lastUpState = false;
    private lastLeftState = false;
    private lastRightState = false;
    private lastAttackState = false;
    private horizontalJustPressed = false;

    // Local player
    private localPlayer: LocalPlayerState | null = null;
    private localPlayerId: string | null = null;
    // Tracks the previous session ID after disconnect, so onInitialState can
    // filter out our own stale session from the server's player list.
    // This prevents ghost clones when the server hasn't processed our old
    // WebSocket close before we rejoin with a new session.
    private staleSessionId: string | null = null;

    // UNIFIED movement timing
    private lastUnifiedMoveTime = 0;
    private lastHorizontalMoveTime = 0;
    private skipNextTickHorizontal = false; // Set by instant move to skip next global tick's horizontal
    private pendingLadderClimb = false; // Buffered UP tap on ladder (consumed on next tick-ready frame)
    private pendingLadderDescend = false; // Buffered DOWN tap on ladder (consumed on next tick-ready frame)
    private pendingLadderHorizontalDir = 0; // Buffered horizontal exit direction on ladder (-1/+1, consumed on tick-ready)
    private wasClimbingLadder = false; // True while continuously ascending a ladder (for seamless top-exit jump)

    // Global aligned fall tick (syncs with server block falls)
    private lastGlobalFallTick = 0;

    private lastPositionSendTime = 0;

    // Other players
    private otherPlayers: Map<string, PositionQueue> = new Map();

    // Chunk subscription manager
    private chunkManager!: ChunkManager;

    // Client-side falling block prediction (synced with player fall tick)
    private fallingBlocks!: FallingBlockPredictor;
    private get localFallingBlocks(): Set<string> { return this.fallingBlocks.localFallingBlocks; }
    private set localFallingBlocks(value: Set<string>) { this.fallingBlocks.localFallingBlocks = value; }
    private get freshFallingBlocks(): Set<string> { return this.fallingBlocks.freshFallingBlocks; }
    private set freshFallingBlocks(value: Set<string>) { this.fallingBlocks.freshFallingBlocks = value; }
    private get logInertia(): Map<string, number> { return this.fallingBlocks.logInertia; }
    private set logInertia(value: Map<string, number>) { this.fallingBlocks.logInertia = value; }

    // Queue for server block updates (processed on global tick to sync timing)
    private pendingServerBlockUpdates: Array<{x: number, y: number, tileType: number, action: string, inertiaDir?: number, isLocalEcho?: boolean}> = [];

    // Combat renderer (slash lifecycle + local combat prediction)
    private combatRenderer!: CombatRenderer;
    private get predictedCombatDestroys(): Set<string> { return this.combatRenderer.predictedCombatDestroys; }
    private set predictedCombatDestroys(value: Set<string>) { this.combatRenderer.predictedCombatDestroys = value; }
    private get activeSlashes() { return this.combatRenderer.activeSlashes; }
    private get slashSprites() { return this.combatRenderer.slashSprites; }
    private get nextLocalSlashId(): number { return this.combatRenderer.nextLocalSlashId; }
    private set nextLocalSlashId(value: number) { this.combatRenderer.nextLocalSlashId = value; }
    private combatHUD!: CombatHUD;

    // Rune data (hieroglyph indices for RUNE tiles)
    private runeData: Map<string, number> = new Map(); // "x,y" -> hieroglyphIndex

    // Hurt stun — player cannot move for 2 ticks (200ms) and shows hurt sprite
    private hurtUntilTick: number = 0;  // local player: stunned + hurt sprite until this global tick
    private otherPlayerHurtUntilTick: Map<string, number> = new Map();  // remote players

    // Speech bubbles
    private localSpeechBubble: SpeechBubble | null = null;
    private otherPlayerSpeechBubbles: Map<string, SpeechBubble> = new Map();
    private tKey!: Phaser.Input.Keyboard.Key;
    private wKey!: Phaser.Input.Keyboard.Key;
    private aKey!: Phaser.Input.Keyboard.Key;
    private sKey!: Phaser.Input.Keyboard.Key;
    private dKey!: Phaser.Input.Keyboard.Key;
    private pKey!: Phaser.Input.Keyboard.Key;
    private tokiAudio: TokiPonaAudio = new TokiPonaAudio();
    private selectedVoiceKey: string = 'walo';

    // Mouse enemies (client-side prediction)
    private clientMouseSim: MouseSimulation = new MouseSimulation();
    private mouseSprites: Map<number, Phaser.GameObjects.Image> = new Map();
    private mouseLastTick: number = 0;

    // NPC mentor (jan pu — stationary on spawn platform)
    private mentorSprite: Phaser.GameObjects.Image | null = null;
    private mentorBehavior: MentorBehavior | null = null;

    // NPC lover (jan olin — follows player inside exploration house)
    private loverSprite: Phaser.GameObjects.Image | null = null;
    private loverBehavior: LoverBehavior | null = null;

    // NPC hero (jan wawa — autonomous platformer in underground cavern)
    private heroSprite: Phaser.GameObjects.Image | null = null;
    private heroHeldBlockSprite: Phaser.GameObjects.Image | null = null;
    private heroBehavior: HeroBehavior | null = null;

    // Text input overlay state (game world keeps ticking while open)
    private textInputOpen: boolean = false;
    // Dictionary overlay state (game world keeps ticking while open)
    private dictionaryOpen: boolean = false;
    // Palette overlay state (game world keeps ticking while open)
    private paletteOpen: boolean = false;
    // Start menu overlay state (blocks input until dismissed)
    private startMenuOpen: boolean = true;
    // Death screen overlay state (blocks input until dismissed)
    private deathScreenOpen: boolean = false;
    private pendingDeathTeleport: { destination: any; portalCode: number[] } | null = null;

    // Portal teleport transition overlay (tiles portal texture across full screen)
    private portalOverlay: Phaser.GameObjects.TileSprite | null = null;
    private portalOverlayRoomBoxes: Phaser.GameObjects.Graphics | null = null;
    private portalOverlayTiles: Phaser.GameObjects.Image[] = [];
    private portalOverlayPlayer: Phaser.GameObjects.Image | null = null;

    // Zelda-style death animation system (extracted)
    private deathAnim!: DeathAnimationSystem;


    constructor(gameClient: GameClient) {
        super({ key: 'GameScene' });
        this.networkClient = gameClient;
    }

    preload(): void {
        _perf('GameScene.preload() START');

        // ── SPRITE ATLAS (single image + JSON manifest replaces ~1,100 individual PNGs) ──
        this.load.atlas(GameScene.ATLAS_KEY, 'sprites/atlas.png', 'sprites/atlas.json');

        // Toki Pona glyph spritesheet (needed for title screen "kiwen jan") — NOT in atlas
        this.load.spritesheet('toki_glyphs_raw', 'sprites/toki_pona_8x8px.png', {
            frameWidth: 8,
            frameHeight: 8,
        });

        // Starmap (needed for skybox) — NOT in atlas (used as spritesheet)
        this.load.spritesheet('starmap', 'sprites/starmap.png', {
            frameWidth: 8,
            frameHeight: 8,
        });

        this.load.once('complete', () => { _perf('GameScene preload COMPLETE (atlas + spritesheets downloaded)'); });
        _perf('GameScene.preload() END — atlas + 2 spritesheets queued');
    }

    static readonly ATLAS_KEY = 'game_atlas';
    atlasFrames: Set<string> = new Set();
    // Map from rodent creatureId to atlas frame name
    rodentFrameMap: Map<string, string> = new Map();

    createImage(x: number, y: number, key: string): Phaser.GameObjects.Image {
        if (this.atlasFrames.has(key)) {
            return this.add.image(x, y, GameScene.ATLAS_KEY, key);
        }
        return this.add.image(x, y, key);
    }

    createSprite(x: number, y: number, key: string): Phaser.GameObjects.Sprite {
        if (this.atlasFrames.has(key)) {
            return this.add.sprite(x, y, GameScene.ATLAS_KEY, key);
        }
        return this.add.sprite(x, y, key);
    }

    applyTexture(sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite, key: string): void {
        if (this.atlasFrames.has(key)) {
            sprite.setTexture(GameScene.ATLAS_KEY, key);
        } else {
            sprite.setTexture(key);
        }
    }

    private initializeExtractedSystems(): void {
        // Create skybox renderer now that processedTextures are available
        this.skybox = new SkyboxRenderer(this, this.processedTextures);
        this.skybox.generateTexture();
        // Sky color stays black until the title screen is dismissed (see onStart callback).
        // This prevents a sky-blue flash if the splash is clicked before create() finishes.

        this.deathAnim = new DeathAnimationSystem(this, this.processedTextures, {
            setBackgroundColor: (color) => this.cameras.main.setBackgroundColor(color),
            setSkyboxVisible: (visible) => this.skybox.setSkyboxVisible(visible),
            tintAllTilesBlack: () => {
                for (const key of this.activeTileKeys) {
                    const sprite = this.tileSprites.get(key);
                    if (sprite) sprite.setTint(0x000000);
                }
            },
            setPlayerTint: (color) => this.localPlayerSprite.setTint(color),
            clearPlayerTint: () => this.localPlayerSprite.clearTint(),
            setPlayerVisible: (visible) => this.localPlayerSprite.setVisible(visible),
            hideHeldBlock: () => this.localHeldBlockSprite.setVisible(false),
            hideSword: () => this.combatHUD.renderLocalSword(false, 0, 0, 0, false, 0, 0),
            onAnimationFinished: () => {
                this.deathScreenOpen = true;
                this.scene.launch('DeathScreenScene', {
                    onDismiss: () => this.handleDeathDismiss(),
                    audio: this.tokiAudio,
                });
            },
        });

        this.fallingBlocks = new FallingBlockPredictor({
            getTile: (x, y) => this.getTile(x, y),
            setLocalTile: (x, y, type) => this.setLocalTile(x, y, type),
        });

        this.combatRenderer = new CombatRenderer(
            {
                getTile: (x, y) => this.getTile(x, y),
                setLocalTile: (x, y, type) => this.setLocalTile(x, y, type),
                isBodyPassable,
            },
            this.fallingBlocks,
            {
                localPlayerId: () => this.localPlayerId,
                lastHorizontalMoveTime: () => this.lastHorizontalMoveTime,
            },
        );
    }

    /**
     * Generate deferred textures: sign glyphs and base rodent standalone textures.
     * Sign glyphs are composed from the toki_glyphs spritesheet (not the atlas).
     * Base rodent textures are extracted from atlas frames for pixel manipulation.
     * Processed in batches via setTimeout to avoid blocking the main thread.
     */
    private generateDeferredTextures(): void {
        _perf('deferred: generating sign glyphs + base rodent textures START');

        // Extract base rodent textures from atlas as standalone canvases
        // (needed for hue shifting and other pixel manipulation)
        for (const base of ['mouse', 'rat', 'squirrel', 'crab']) {
            if (this.atlasFrames.has(base)) {
                const canvas = this.textureManager.extractAtlasFrame(base);
                if (canvas) this.textures.addCanvas(`rodent_${base}`, canvas);
            }
        }

        // Sign glyph textures: stone background + glyph overlay in light grey
        const glyphTexture = this.textures.get('toki_glyphs');
        const glyphSource = glyphTexture ? glyphTexture.getSourceImage() as HTMLCanvasElement : null;
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = TILE_SIZE;
        tmpCanvas.height = TILE_SIZE;
        const tmpCtx = tmpCanvas.getContext('2d', { willReadFrequently: true })!;

        const BATCH_SIZE = 20;
        let signIdx = 0;

        const processBatch = () => {
            const signEnd = Math.min(signIdx + BATCH_SIZE, 120);
            for (; signIdx < signEnd; signIdx++) {
                const texKey = `sign_glyph_${signIdx}`;
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d')!;
                ctx.fillStyle = '#696969';
                ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
                if (glyphSource) {
                    const col = signIdx % 12;
                    const row = Math.floor(signIdx / 12);
                    tmpCtx.clearRect(0, 0, TILE_SIZE, TILE_SIZE);
                    tmpCtx.drawImage(glyphSource, col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE, 0, 0, TILE_SIZE, TILE_SIZE);
                    const imgData = tmpCtx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
                    for (let p = 0; p < imgData.data.length; p += 4) {
                        if (imgData.data[p + 3] > 0) {
                            imgData.data[p] = 180;
                            imgData.data[p + 1] = 180;
                            imgData.data[p + 2] = 180;
                        }
                    }
                    tmpCtx.putImageData(imgData, 0, 0);
                    ctx.drawImage(tmpCanvas, 0, 0);
                }
                this.textures.addCanvas(texKey, canvas);
            }

            if (signIdx < 120) {
                setTimeout(processBatch, 0);
            } else {
                _perf('deferred: all texture processing END');
            }
        };

        processBatch();
    }

    create(): void {
        _perf('GameScene.create() START');
        // Audio loading pipeline — 4 sequential phases to prioritize by need:
        //   Phase 1: title_music (1.6 MB) — needed for title screen
        //   Phase 2: all SFX (136 KB) — needed at gameplay start
        //   Phase 3: home_music (6 MB) — background, plays in offline world
        //   Phase 4: night_music (6.3 MB) + death_music (732 KB) — deferred until gameplay
        // Each phase waits for the previous to complete before starting.
        this.load.audio('title_music', 'title_music.mp3');
        this.load.once('complete', () => {
            _perf('Audio phase 1 complete (title_music)');
            this.load.audio('start_sound', 'start_sound.mp3');
            this.load.audio('teleport_sound', 'teleport.mp3');
            this.load.audio('death_sound', 'death_sound.mp3');
            this.load.audio('equip_boots', 'sprites/equip_boots.mp3');
            this.load.audio('got_item', 'sprites/got_item.mp3');
            this.load.audio('golden_key', 'sprites/golden_key.mp3');
            this.load.once('complete', () => {
                _perf('Audio phase 2 complete (SFX)');
                this.load.audio('home_music', 'home_music.mp3');
                this.load.once('complete', () => {
                    _perf('Audio phase 3 complete (home_music)');
                    // Only trigger home music if title screen has already been dismissed.
                    // Otherwise startHomeMusic() will play it when the title is dismissed.
                    if (!this.startMenuOpen) {
                        this.skybox.setHomeMusicLoaded();
                    }
                    this.loadPhase4Audio();
                });
                this.load.start();
            });
            this.load.start();
        });
        this.load.start();

        _perf('create(): generating simple textures (sky, flint, sapling, water, etc.)');
        // Create sky texture (light blue - for dictionary display)
        const skyGraphics = this.add.graphics();
        skyGraphics.fillStyle(0x87CEEB, 1);
        skyGraphics.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
        skyGraphics.generateTexture('sky_generated', TILE_SIZE, TILE_SIZE);
        skyGraphics.destroy();

        // flint texture now loaded as sprite (flint.png)

        // Create sapling texture (light green)
        const saplingGraphics = this.add.graphics();
        saplingGraphics.fillStyle(0x90EE90, 1);
        saplingGraphics.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
        saplingGraphics.generateTexture('sapling_generated', TILE_SIZE, TILE_SIZE);
        saplingGraphics.destroy();

        // Create rats_nest texture (dark green grass with hole)
        {
            const canvas = document.createElement('canvas');
            canvas.width = TILE_SIZE;
            canvas.height = TILE_SIZE;
            const rctx = canvas.getContext('2d')!;

            // Draw grass sprite as base (from atlas), tinted dark green
            const grassAtlas = this.textures.get(GameScene.ATLAS_KEY);
            const grassFrame = grassAtlas?.get('grass');
            if (grassFrame && grassFrame.name !== '__BASE') {
                const grassSrc = grassAtlas.getSourceImage() as HTMLImageElement;
                rctx.drawImage(grassSrc, grassFrame.cutX, grassFrame.cutY, grassFrame.cutWidth, grassFrame.cutHeight, 0, 0, TILE_SIZE, TILE_SIZE);
                // Tint toward dark green by multiplying with a dark green overlay
                const imgData = rctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
                const d = imgData.data;
                for (let i = 0; i < d.length; i += 4) {
                    d[i]     = Math.floor(d[i] * 0.25);     // reduce red
                    d[i + 1] = Math.floor(d[i + 1] * 0.55); // keep some green
                    d[i + 2] = Math.floor(d[i + 2] * 0.2);  // reduce blue
                }
                rctx.putImageData(imgData, 0, 0);
            } else {
                // Fallback: solid dark green
                rctx.fillStyle = '#1a3a0a';
                rctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
            }

            // Draw hole (dark oval in center)
            rctx.fillStyle = '#0a0a0a';
            rctx.fillRect(2, 3, 4, 3);   // 4x3 dark core
            rctx.fillStyle = '#151505';
            rctx.fillRect(1, 4, 1, 1);   // left edge softening
            rctx.fillRect(6, 4, 1, 1);   // right edge softening

            this.textures.addCanvas('rats_nest_generated', canvas);
        }

        // Create water texture (blue)
        const waterGraphics = this.add.graphics();
        waterGraphics.fillStyle(0x1E90FF, 1);
        waterGraphics.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
        waterGraphics.generateTexture('water_generated', TILE_SIZE, TILE_SIZE);
        waterGraphics.destroy();

        // Create stone club texture (grey)
        const stoneClubGraphics = this.add.graphics();
        stoneClubGraphics.fillStyle(0x808080, 1);
        stoneClubGraphics.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
        stoneClubGraphics.generateTexture('stone_club_generated', TILE_SIZE, TILE_SIZE);
        stoneClubGraphics.destroy();

        // Create underground darkness texture (opaque black for sub-stone-level sky tiles)
        const undergroundGraphics = this.add.graphics();
        undergroundGraphics.fillStyle(0x000000, 1);
        undergroundGraphics.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
        undergroundGraphics.generateTexture('underground_generated', TILE_SIZE, TILE_SIZE);
        undergroundGraphics.destroy();

        // red_stone texture now loaded as sprite (red_stone.png)

        // Generate fallback lover texture (pink 8x16) in case sprite isn't in atlas
        if (!this.atlasFrames.has('lover_right')) {
            const lg = this.add.graphics();
            lg.fillStyle(0xFF69B4, 1);
            lg.fillRect(0, 0, TILE_SIZE, TILE_SIZE * 2);
            lg.generateTexture('lover_right', TILE_SIZE, TILE_SIZE * 2);
            lg.destroy();
        }

        // Generate heart container textures
        {
            const g = this.add.graphics();
            g.fillStyle(0x90EE90, 1);
            g.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
            g.generateTexture('heart_container', TILE_SIZE, TILE_SIZE);
            g.clear();
            g.fillStyle(0x8B008B, 1);
            g.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
            g.generateTexture('big_heart_container', TILE_SIZE, TILE_SIZE);
            g.destroy();
        }

        // Build atlasFrames set from the loaded atlas
        _perf('create(): building atlasFrames set');
        {
            const atlas = this.textures.get(GameScene.ATLAS_KEY);
            const frameNames = atlas.getFrameNames();
            for (const name of frameNames) {
                if (name !== '__BASE') this.atlasFrames.add(name);
            }
        }

        // Build rodentFrameMap from CREATURE_SPRITE_FILES
        for (const [filename, creatureId] of CREATURE_SPRITE_FILES) {
            this.rodentFrameMap.set(creatureId, filename);
        }

        // Process all textures (atlas frame mapping, glyph processing)
        _perf('create(): TextureManager.processAll() START');
        this.textureManager = new TextureManager(this, this.atlasFrames);
        this.textureManager.processAll();
        // KASI sprites: use atlas frames directly (no extraction needed)
        for (const pt of PLACEHOLDER_TILES) {
            if (pt.word1 !== 23) continue; // word1=23 is kasi category
            const enumName = TileType[pt.type]?.toLowerCase();
            if (enumName && this.atlasFrames.has(enumName)) {
                this.kasiProcessedTextures.set(pt.type, enumName);
            }
        }
        _perf('create(): TextureManager.processAll() END');

        _perf('create(): initializeExtractedSystems() START');
        this.initializeExtractedSystems();
        _perf('create(): initializeExtractedSystems() END');

        _perf('create(): generating composite textures (rune, shield, placeholders)');
        // Create rune texture: dark grey-purple background with colored "ale" glyph overlay
        {
            const canvas = document.createElement('canvas');
            canvas.width = TILE_SIZE;
            canvas.height = TILE_SIZE;
            const ctx = canvas.getContext('2d')!;

            // Fill background
            ctx.fillStyle = '#3D2F4A';
            ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);

            // Extract the "ale" glyph (index 4) from the processed toki_glyphs spritesheet
            const glyphTexture = this.textures.get('toki_glyphs');
            if (glyphTexture) {
                const glyphSource = glyphTexture.getSourceImage() as HTMLCanvasElement;
                const aleIndex = 4; // "ale" = index 4
                const col = aleIndex % 12;
                const row = Math.floor(aleIndex / 12);
                const gx = col * TILE_SIZE;
                const gy = row * TILE_SIZE;

                // Draw glyph to a temp canvas to tint it
                const tmp = document.createElement('canvas');
                tmp.width = TILE_SIZE;
                tmp.height = TILE_SIZE;
                const tmpCtx = tmp.getContext('2d', { willReadFrequently: true })!;
                tmpCtx.drawImage(glyphSource, gx, gy, TILE_SIZE, TILE_SIZE, 0, 0, TILE_SIZE, TILE_SIZE);

                // Tint white glyph pixels to ale's color [110, 68, 255]
                const imgData = tmpCtx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
                const d = imgData.data;
                for (let i = 0; i < d.length; i += 4) {
                    if (d[i + 3] > 0) {
                        d[i] = 110;     // R
                        d[i + 1] = 68;  // G
                        d[i + 2] = 255; // B
                    }
                }
                tmpCtx.putImageData(imgData, 0, 0);

                // Composite tinted glyph onto rune background
                ctx.drawImage(tmp, 0, 0);
            }

            this.textures.addCanvas('rune_generated', canvas);
        }

        // Create wooden shield texture: trunk-colored background with red "awen" glyph overlay
        {
            const canvas = document.createElement('canvas');
            canvas.width = TILE_SIZE;
            canvas.height = TILE_SIZE;
            const ctx = canvas.getContext('2d')!;

            // Fill with trunk/wood brown background
            ctx.fillStyle = '#5C3317';
            ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);

            // Extract the "awen" glyph (index 8) from the processed toki_glyphs spritesheet
            const glyphTexture = this.textures.get('toki_glyphs');
            if (glyphTexture) {
                const glyphSource = glyphTexture.getSourceImage() as HTMLCanvasElement;
                const awenIndex = 8; // "awen" = index 8
                const col = awenIndex % 12;
                const row = Math.floor(awenIndex / 12);
                const gx = col * TILE_SIZE;
                const gy = row * TILE_SIZE;

                const tmp = document.createElement('canvas');
                tmp.width = TILE_SIZE;
                tmp.height = TILE_SIZE;
                const tmpCtx = tmp.getContext('2d', { willReadFrequently: true })!;
                tmpCtx.drawImage(glyphSource, gx, gy, TILE_SIZE, TILE_SIZE, 0, 0, TILE_SIZE, TILE_SIZE);

                // Tint white glyph pixels to red
                const imgData = tmpCtx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
                const d = imgData.data;
                for (let i = 0; i < d.length; i += 4) {
                    if (d[i + 3] > 0) {
                        d[i] = 220;     // R
                        d[i + 1] = 40;  // G
                        d[i + 2] = 40;  // B
                    }
                }
                tmpCtx.putImageData(imgData, 0, 0);

                ctx.drawImage(tmp, 0, 0);
            }

            this.textures.addCanvas('wooden_shield_generated', canvas);
        }

        _perf(`create(): generating placeholder textures (${PLACEHOLDER_TILES.length} tiles) START`);
        // Generate placeholder textures for data-driven tiles that don't have atlas frames.
        // Sand tiles are skipped — they get dedicated grainy textures in the sand loop.
        {
            const glyphTexture = this.textures.get('toki_glyphs');
            const glyphSource = glyphTexture ? glyphTexture.getSourceImage() as HTMLCanvasElement : null;
            for (const pt of PLACEHOLDER_TILES) {
                if (isSandTile(pt.type)) continue;
                if (pt.word1 === 23) continue; // KASI: use atlas frames, not placeholders
                // Skip tiles that have atlas frames — they'll be used directly
                const enumName = TileType[pt.type]?.toLowerCase();
                if (enumName && this.atlasFrames.has(enumName)) continue;
                const texKey = `placeholder_${pt.type}`;
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d')!;
                const r = (pt.color >> 16) & 0xFF;
                const g = (pt.color >> 8) & 0xFF;
                const b = pt.color & 0xFF;
                ctx.fillStyle = `rgb(${r},${g},${b})`;
                ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
                if (glyphSource) {
                    const col = pt.word2 % 12;
                    const row = Math.floor(pt.word2 / 12);
                    ctx.drawImage(glyphSource, col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE, 0, 0, TILE_SIZE, TILE_SIZE);
                }
                this.textures.addCanvas(texKey, canvas);
            }
        }

        _perf('create(): placeholder textures END');

        // Creature sprite processing is deferred to after first frame (see deferredTextureGeneration).
        // SceneRenderSystem falls back to colored squares for creatures without processed sprites.

        _perf('create(): generating sand textures (31 types) START');
        // Generate noisy grainy sand textures for ko (sand) types
        // Each sand has a unique base color + grain noise matching its concept
        {
            // Simple seeded PRNG for deterministic noise
            const sandRng = (seed: number) => {
                let s = seed;
                return () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
            };

            // Sand texture definitions: [TileType, baseR, baseG, baseB, grainR, grainG, grainB, noiseAmount]
            // noiseAmount: how much grain variation (0-1), higher = more speckled
            const sandDefs: [number, number, number, number, number, number, number, number][] = [
                // KO_ESUN - Salt: white crystalline with grey specks
                [TileType.KO_ESUN, 235, 235, 235, 200, 200, 210, 0.4],
                // KO_IJO - Sand: classic warm sandy tan
                [TileType.KO_IJO, 210, 180, 120, 180, 150, 90, 0.35],
                // KO_IKE - Quicksand: wet dark sand, murky brownish
                [TileType.KO_IKE, 140, 115, 70, 110, 85, 50, 0.5],
                // KO_ILO - Sawdust: pale golden wood dust
                [TileType.KO_ILO, 220, 195, 140, 190, 165, 110, 0.45],
                // KO_JAKI - Guano: brownish-white crumbly
                [TileType.KO_JAKI, 180, 170, 140, 140, 130, 100, 0.5],
                // KO_JAN - Cremation Ash: pale grey with white flecks
                [TileType.KO_JAN, 160, 155, 150, 190, 185, 180, 0.45],
                // KO_JELO - Sulfur: vivid yellow with darker yellow specks
                [TileType.KO_JELO, 220, 210, 50, 200, 180, 30, 0.4],
                // KO_KASI - Topsoil: rich dark brown with organic bits
                [TileType.KO_KASI, 100, 75, 45, 80, 55, 30, 0.45],
                // KO_KIWEN - Gravel: grey with multicolor pebble specks
                [TileType.KO_KIWEN, 140, 135, 130, 110, 105, 100, 0.55],
                // KO_KO - Sand: golden desert sand
                [TileType.KO_KO, 215, 185, 125, 195, 160, 100, 0.35],
                // KO_KULE - Concrete Powder: light grey with color speckles
                [TileType.KO_KULE, 190, 190, 195, 160, 170, 180, 0.5],
                // KO_KULUPU - Aggregate: grey with varied stone chips
                [TileType.KO_KULUPU, 155, 150, 145, 130, 120, 115, 0.55],
                // KO_LAPE - Sleep Sand: golden sparkly, warm
                [TileType.KO_LAPE, 220, 195, 100, 240, 220, 140, 0.4],
                // KO_LILI - Dust: pale grey-brown, very fine
                [TileType.KO_LILI, 185, 175, 160, 170, 160, 145, 0.3],
                // KO_LOJE - Red Sand: reddish-orange sand
                [TileType.KO_LOJE, 190, 100, 60, 160, 75, 40, 0.4],
                // KO_MOKU - Flour: off-white powdery
                [TileType.KO_MOKU, 240, 235, 220, 225, 220, 205, 0.25],
                // KO_MUN - Moon Dust: silvery grey, fine
                [TileType.KO_MUN, 180, 185, 195, 160, 165, 175, 0.35],
                // KO_NI - Suspicious Sand: tan with darker mystery specks
                [TileType.KO_NI, 200, 175, 120, 170, 140, 80, 0.5],
                // KO_O - Pocket Sand: classic sandy beige
                [TileType.KO_O, 210, 190, 140, 190, 170, 120, 0.4],
                // KO_PAN - Flour: cream-white, soft
                [TileType.KO_PAN, 238, 232, 215, 222, 216, 200, 0.25],
                // KO_PIMEJA - Black Sand: dark volcanic sand
                [TileType.KO_PIMEJA, 45, 42, 40, 30, 28, 25, 0.4],
                // KO_PU - Chalk Dust: very pale white-grey
                [TileType.KO_PU, 230, 228, 225, 215, 212, 210, 0.3],
                // KO_SELI - Volcanic Ash: dark grey with warm undertone
                [TileType.KO_SELI, 95, 85, 80, 75, 65, 55, 0.45],
                // KO_SEWI - Fairy Dust: pastel iridescent shimmer
                [TileType.KO_SEWI, 200, 180, 220, 180, 200, 230, 0.5],
                // KO_SUWI - Sugar: pure white granular
                [TileType.KO_SUWI, 245, 245, 245, 230, 230, 235, 0.3],
                // KO_TELO - Mud: dark wet brown
                [TileType.KO_TELO, 105, 80, 55, 85, 60, 35, 0.45],
                // KO_TENPO - Hourglass Sand: warm golden fine sand
                [TileType.KO_TENPO, 225, 200, 130, 205, 180, 110, 0.35],
                // KO_UNPA - Pollen: bright yellow-green dust
                [TileType.KO_UNPA, 210, 205, 60, 190, 185, 40, 0.4],
                // KO_UTALA - Gunpowder: dark charcoal with grey specks
                [TileType.KO_UTALA, 55, 55, 55, 75, 75, 70, 0.5],
                // KO_WALO - White Sand: bright white with subtle grain
                [TileType.KO_WALO, 240, 238, 232, 225, 222, 218, 0.3],
                // KO_WASO - Guano: whitish-brown chalky
                [TileType.KO_WASO, 195, 185, 165, 165, 155, 135, 0.45],
            ];

            for (const [tileType, bR, bG, bB, gR, gG, gB, noise] of sandDefs) {
                const texKey = `placeholder_${tileType}`;
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
                const imgData = ctx.createImageData(TILE_SIZE, TILE_SIZE);
                const d = imgData.data;
                const rng = sandRng(tileType * 7919);

                for (let py = 0; py < TILE_SIZE; py++) {
                    for (let px = 0; px < TILE_SIZE; px++) {
                        const i = (py * TILE_SIZE + px) * 4;
                        const t = rng();
                        if (t < noise) {
                            // Grain pixel — use grain color with slight variation
                            const v = (rng() - 0.5) * 30;
                            d[i] = Math.max(0, Math.min(255, gR + v));
                            d[i + 1] = Math.max(0, Math.min(255, gG + v));
                            d[i + 2] = Math.max(0, Math.min(255, gB + v));
                        } else {
                            // Base pixel with subtle noise
                            const v = (rng() - 0.5) * 20;
                            d[i] = Math.max(0, Math.min(255, bR + v));
                            d[i + 1] = Math.max(0, Math.min(255, bG + v));
                            d[i + 2] = Math.max(0, Math.min(255, bB + v));
                        }
                        d[i + 3] = 255;
                    }
                }

                ctx.putImageData(imgData, 0, 0);
                this.textures.addCanvas(texKey, canvas);
            }
        }

        _perf('create(): sand textures END');

        _perf('create(): generating misc textures (dice, plates, signs, etc.) START');
        // Generate dice face textures (ILO_ANU): 6 faces with dot patterns on grey
        {
            const dotPositions: { [face: number]: [number, number][] } = {
                1: [[3, 3]],
                2: [[1, 1], [5, 5]],
                3: [[1, 1], [3, 3], [5, 5]],
                4: [[1, 1], [5, 1], [1, 5], [5, 5]],
                5: [[1, 1], [5, 1], [3, 3], [1, 5], [5, 5]],
                6: [[1, 1], [5, 1], [1, 3], [5, 3], [1, 5], [5, 5]],
            };
            for (let face = 1; face <= 6; face++) {
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d')!;
                // Grey background
                ctx.fillStyle = '#C0C0C0';
                ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
                // Dark border
                ctx.fillStyle = '#808080';
                ctx.fillRect(0, 0, TILE_SIZE, 1);
                ctx.fillRect(0, 7, TILE_SIZE, 1);
                ctx.fillRect(0, 0, 1, TILE_SIZE);
                ctx.fillRect(7, 0, 1, TILE_SIZE);
                // Black dots
                ctx.fillStyle = '#000000';
                for (const [dx, dy] of dotPositions[face]) {
                    ctx.fillRect(dx, dy, 2, 2);
                }
                this.textures.addCanvas(`dice_face_${face}`, canvas);
            }
        }

        // Pressure plate active, eye switch active, and question block inert textures
        // are now loaded as sprites (kiwen_la_active.png, kiwen_lukin_active.png, kiwen_seme_inert.png)

        // Sign glyph textures are deferred to after first frame (see deferredTextureGeneration).
        // SceneRenderSystem falls back to getTileTexture() for signs without generated glyphs.

        // Generate KASI_KIWEN (Petrified Wood) texture: greyscale version of trunk
        {
            const trunkAtlas = this.textures.get(GameScene.ATLAS_KEY);
            const trunkFrame = trunkAtlas?.get('trunk');
            if (trunkFrame && trunkFrame.name !== '__BASE') {
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
                const trunkSrc = trunkAtlas.getSourceImage() as HTMLImageElement;
                ctx.drawImage(trunkSrc, trunkFrame.cutX, trunkFrame.cutY, trunkFrame.cutWidth, trunkFrame.cutHeight, 0, 0, TILE_SIZE, TILE_SIZE);
                const imgData = ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
                for (let p = 0; p < imgData.data.length; p += 4) {
                    const grey = Math.round(imgData.data[p] * 0.299 + imgData.data[p + 1] * 0.587 + imgData.data[p + 2] * 0.114);
                    imgData.data[p] = grey;
                    imgData.data[p + 1] = grey;
                    imgData.data[p + 2] = grey;
                }
                ctx.putImageData(imgData, 0, 0);
                this.textures.addCanvas('kasi_kiwen', canvas);
            }
        }

        // Generate KASI_MA (Potato in Dirt) texture: dirt background with potato dots
        {
            const dirtAtlas = this.textures.get(GameScene.ATLAS_KEY);
            const dirtFrame = dirtAtlas?.get('dirt');
            if (dirtFrame && dirtFrame.name !== '__BASE') {
                const canvas = document.createElement('canvas');
                canvas.width = TILE_SIZE;
                canvas.height = TILE_SIZE;
                const ctx = canvas.getContext('2d')!;
                const dirtSrc = dirtAtlas.getSourceImage() as HTMLImageElement;
                ctx.drawImage(dirtSrc, dirtFrame.cutX, dirtFrame.cutY, dirtFrame.cutWidth, dirtFrame.cutHeight, 0, 0, TILE_SIZE, TILE_SIZE);
                // Draw potato spots (tan/yellow ovals)
                ctx.fillStyle = '#C8A860';
                ctx.fillRect(2, 3, 3, 2);
                ctx.fillRect(5, 5, 2, 2);
                this.textures.addCanvas('kasi_ma', canvas);
            }
        }

        _perf('create(): misc textures END');

        // Schedule deferred texture generation for sign glyphs and base rodent textures.
        // All sprites are already in the atlas — no downloading needed.
        this.time.delayedCall(100, () => this.generateDeferredTextures());

        // Depth layers:
        // Background = sky color (behind everything)
        // Crouching players at depth 5 (behind block tiles, above sky)
        // Block tiles at depth 10
        // Other players at depth 50
        // Other players' held blocks at depth 55
        // Standing local player at depth 100
        // Local held block at depth 110

        _perf('create(): setting up game objects + input + HUD');
        // Create local player sprite (hidden until we have position)
        this.localPlayerSprite = this.createSprite(0, 0, this.processedTextures.man_right);
        this.localPlayerSprite.setOrigin(0, 0);
        this.localPlayerSprite.setScale(1);
        this.localPlayerSprite.setVisible(false);
        this.localPlayerSprite.setDepth(100);

        // Create local held block sprite (hidden until holding)
        this.localHeldBlockSprite = this.createImage(0, 0, this.processedTextures.dirt);
        this.localHeldBlockSprite.setOrigin(0, 0);
        this.localHeldBlockSprite.setDepth(110);
        this.localHeldBlockSprite.setVisible(false);

        // Input
        this.cursors = this.input.keyboard!.createCursorKeys();
        this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
        this.tKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.T);
        this.wKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W);
        this.aKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A);
        this.sKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S);
        this.dKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D);
        this.pKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.P);
        this.touchControls = new TouchControls(this);
        this.touchControls.create();

        // Audio: set up gesture unlock for mobile (must happen before any touch).
        // Actual audio data loading (874KB sprite + 50KB manifest) is deferred
        // to Phase 3 (after title screen dismissed) to avoid bandwidth competition.
        this.tokiAudio.setupUserGestureUnlock();

        // Register TextInputScene for toki pona text input overlay
        if (!this.scene.get('TextInputScene')) {
            this.scene.add('TextInputScene', TextInputScene, false);
        }

        // Register DictionaryScene for toki pona dictionary overlay
        if (!this.scene.get('DictionaryScene')) {
            this.scene.add('DictionaryScene', DictionaryScene, false);
        }

        // Register GlyphPickerScene (120-glyph quick-select, launched from dictionary)
        if (!this.scene.get('GlyphPickerScene')) {
            this.scene.add('GlyphPickerScene', GlyphPickerScene, false);
        }

        // Register SpriteBrowserScene (120-item sprite/glyph browser, launched from dictionary)
        if (!this.scene.get('SpriteBrowserScene')) {
            this.scene.add('SpriteBrowserScene', SpriteBrowserScene, false);
        }

        // Register palette overlay (launched by P key — biome palette browser)
        if (!this.scene.get('PaletteScene')) {
            this.scene.add('PaletteScene', PaletteScene, false);
        }

        // Register death screen overlay (launched on player death)
        if (!this.scene.get('DeathScreenScene')) {
            this.scene.add('DeathScreenScene', DeathScreenScene, false);
        }

        // Register start menu overlay
        if (!this.scene.get('StartMenuScene')) {
            this.scene.add('StartMenuScene', StartMenuScene, false);
        }

        // The HTML splash screen (index.html) covers the page instantly before Phaser loads.
        // When the user interacts, 'splash-dismissed' fires — but the splash stays visible
        // until the title screen has rendered to prevent blank sky-blue frames.
        const launchStartMenu = () => {
            _perf('Launching StartMenuScene (title screen)');
            this.scene.launch('StartMenuScene', {
                onStart: () => {
                    _perf('StartMenuScene dismissed — entering gameplay');
                    this.startMenuOpen = false;
                    // Now reveal the sky — background was kept black through splash + title
                    this.skybox.updateSkyColor(this.networkClient.getMode() === 'online');
                    // Play start sound effect
                    if (this.cache.audio.exists('start_sound')) {
                        this.sound.play('start_sound', { volume: 0.2 });
                    }
                    // Start home music loop (will auto-start when Phase 3 finishes if still loading)
                    this.skybox.startHomeMusic();
                },
            });
            // Hide the splash after one frame so StartMenuScene has rendered beneath it
            this.time.delayedCall(0, () => {
                this.game.events.emit('hide-splash');
            });
        };
        this.game.events.once('splash-dismissed', launchStartMenu);
        // If splash was already dismissed before scene created, launch immediately
        if (document.getElementById('splash')?.dataset.dismissed === 'true') {
            this.game.events.off('splash-dismissed', launchStartMenu);
            launchStartMenu();
        }

        // Micro font (needed by CombatHUD version label)
        createMicroFont(this);

        // Combat HUD (hearts + swords)
        this.combatHUD = new CombatHUD(this, this.processedTextures);
        this.combatHUD.create();

        // Chunk subscription manager
        this.chunkManager = new ChunkManager(
            (chunks) => this.networkClient.subscribeToChunks(chunks),
        );

        // Tile inspection: click a tile to speak its name and remember for dictionary
        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            if (this.startMenuOpen || this.deathScreenOpen || this.deathAnim.isActive || this.textInputOpen || this.dictionaryOpen || this.paletteOpen || !this.localPlayer) return;
            if (this.inputState.attack) return;

            const { offsetX, offsetY } = this.getCameraOffset();
            const worldX = Math.floor((pointer.x - offsetX) / TILE_SIZE);
            const worldY = Math.floor((pointer.y - offsetY) / TILE_SIZE);
            const tileType = this.getTile(worldX, worldY);

            const wordPair = TILE_DICTIONARY_MAP[tileType];
            if (!wordPair) return;

            const words = [wordPair[0], wordPair[1]];

            // Speak the tile name (send to server + create local bubble)
            this.networkClient.sendSpeech(words, this.selectedVoiceKey);

            // Remember for dictionary auto-navigation
            DictionaryScene.lastInspectedA = wordPair[0];
            DictionaryScene.lastInspectedB = wordPair[1];

            // Add to T menu message history
            TextInputScene.addToHistory(words);
        });

        // Network callbacks
        this.setupNetworkCallbacks();

        // Signal that scene is ready for connections
        _perf('GameScene.create() COMPLETE — emitting scene-ready');
        this.game.events.emit('scene-ready');
    }

    private setupNetworkCallbacks(): void {
        setupSceneNetworkCallbacks(this as any);
    }

    /**
     * Phase 4 audio loading: night music (6.3 MB), death music (732 KB), and toki pona voice audio.
     * Chained after Phase 3 (home_music) completes. These are the lowest-priority audio files.
     */
    private loadPhase4Audio(): void {
        _perf('Audio phase 4 START (night_music + death_music + toki voice)');
        this.load.audio('night_music', 'night_music.mp3');
        this.load.audio('death_music', 'death_music.mp3');
        this.load.once('complete', () => { _perf('Audio phase 4 complete (night/death music)'); this.skybox.setNightMusicLoaded(); });
        this.load.start();
        // Also start loading toki pona voice audio (874KB sprite + 50KB manifest)
        this.tokiAudio.load().catch(() => {});
    }

    /**
     * Clear game state when teleporting to a new world
     */
    private handleDeathDismiss(): void {
        this.deathScreenOpen = false;

        // Restore world visuals (kept black during death screen to avoid flash)
        for (const [, sprite] of this.tileSprites) {
            sprite.clearTint();
        }
        this.skybox.updateSkyColor(this.networkClient.getMode() === 'online');
        this.skybox.setSkyboxVisible(true);

        if (this.pendingDeathTeleport) {
            // Online death: server sent a teleport (back to offline world)
            const { destination, portalCode } = this.pendingDeathTeleport;
            this.pendingDeathTeleport = null;
            // Override spawn to mentor house if player has visited it.
            // Use the SAVED local seed — this.worldSeed is the server's seed
            // at this point, which gives the wrong Y for the local world.
            if (this.hasVisitedMentorHouse) {
                const localSeed = this.networkClient.getLocalSeed() ?? this.worldSeed;
                const portalY = calculatePortalSurfaceY(localSeed);
                destination.spawnX = SPAWN_CENTER_X;
                destination.spawnY = portalY - 5;
            }
            console.log(`[DEATH DISMISS] Executing deferred teleport -> ${destination.worldId}`);
            if (this.cache.audio.exists('teleport_sound')) {
                this.sound.play('teleport_sound', { volume: 0.2 });
            }
            this.showPortalOverlay();
            this.networkClient.teleportTo(destination, () => this.clearGameState()).catch((error) => {
                console.error('[DEATH DISMISS] teleportTo failed:', error);
                this.hidePortalOverlay();
            });
        } else if (this.localPlayer) {
            // Offline death: respawn at mentor house if visited, otherwise underground cavern
            if (this.hasVisitedMentorHouse) {
                const portalY = calculatePortalSurfaceY(this.worldSeed);
                this.localPlayer.x = SPAWN_CENTER_X;
                this.localPlayer.y = portalY - 5;
            } else {
                this.localPlayer.x = UNDERGROUND_SPAWN_X;
                this.localPlayer.y = UNDERGROUND_SPAWN_Y;
            }
            this.localPlayer.hp = this.localPlayer.maxHp;
            this.localPlayer.airTicks = MAX_AIR_TICKS;
            this.localPlayer.state = PlayerState.FALLING;
            this.localPlayer.isHoldingSword = false;
            this.localPlayer.isHoldingBlock = false;
            this.localPlayer.heldBlockType = null;
            this.combatHUD.updateHeartDisplay(this.localPlayer.hp, this.localPlayer.maxHp);
            this.combatHUD.updateBubbleDisplay(this.localPlayer.airTicks);
            // Restore player sprite visibility (hidden during blacksky phase)
            this.localPlayerSprite.setVisible(true);
        }
    }

    /** Create MentorBehavior once worldSeed is known (called from onInitialState). */
    private initMentorBehavior(): void {
        if (this.mentorBehavior) {
            this.mentorBehavior.destroy();
        }
        this.mentorBehavior = new MentorBehavior(this, this.tokiAudio, this.worldSeed);
    }

    /** Create LoverBehavior once worldSeed is known (called from onInitialState). */
    private initLoverBehavior(): void {
        if (this.loverBehavior) {
            this.loverBehavior.destroy();
        }
        this.loverBehavior = new LoverBehavior(this, this.tokiAudio, this.worldSeed);
    }

    /** Create HeroBehavior once worldSeed is known (called from onInitialState). */
    private initHeroBehavior(): void {
        if (this.heroBehavior) {
            this.heroBehavior.destroy();
        }
        // Provide a world access wrapper using the scene's chunk cache
        const heroWorld = {
            getTile: (x: number, y: number) => this.getTile(x, y),
            setTile: (x: number, y: number, type: TileType) => this.setLocalTile(x, y, type),
            notifyFallingBlock: (x: number, y: number) => this.fallingBlocks.propagateLocalInstabilityUp(x, y),
            isChunkLoaded: (x: number, y: number) => {
                const cx = Math.floor(x / CHUNK_SIZE);
                const cy = Math.floor(y / CHUNK_SIZE);
                return this.chunks.has(`${cx},${cy}`);
            },
        };
        this.heroBehavior = new HeroBehavior(heroWorld, this, this.tokiAudio);
    }

    private clearGameState(): void {
        // Stop music when leaving the server
        this.skybox.stopNightMusic();
        this.skybox.stopHomeMusic();

        // Clear chunks
        this.chunks.clear();
        this.chunkManager.reset();

        // Clear other players
        for (const [id, sprite] of this.otherPlayerSprites) {
            sprite.destroy();
        }
        this.otherPlayerSprites.clear();
        this.otherPlayers.clear();

        for (const [id, sprite] of this.otherPlayerHeldBlocks) {
            sprite.destroy();
        }
        this.otherPlayerHeldBlocks.clear();

        // Clear combat HUD (other player swords)
        this.combatHUD.clear();

        // Clear slashes
        for (const [id, sprite] of this.slashSprites) {
            sprite.destroy();
        }
        this.slashSprites.clear();
        this.activeSlashes.clear();

        // Clear tile sprites
        for (const [key, sprite] of this.tileSprites) {
            sprite.destroy();
        }
        this.tileSprites.clear();
        this.activeTileKeys.clear();

        // Clear rune data
        this.runeData.clear();

        // Clear speech bubbles
        if (this.localSpeechBubble) {
            this.localSpeechBubble.destroy();
            this.localSpeechBubble = null;
        }
        for (const [id, bubble] of this.otherPlayerSpeechBubbles) {
            bubble.destroy();
        }
        this.otherPlayerSpeechBubbles.clear();

        // Clear mice
        this.clientMouseSim.clear();
        for (const [id, sprite] of this.mouseSprites) {
            sprite.destroy();
        }
        this.mouseSprites.clear();

        // Clear mentor
        if (this.mentorBehavior) {
            this.mentorBehavior.destroy();
            this.mentorBehavior = null;
        }
        if (this.mentorSprite) {
            this.mentorSprite.destroy();
            this.mentorSprite = null;
        }

        // Clear lover
        if (this.loverBehavior) {
            this.loverBehavior.destroy();
            this.loverBehavior = null;
        }
        if (this.loverSprite) {
            this.loverSprite.destroy();
            this.loverSprite = null;
        }

        // Clear hero
        if (this.heroBehavior) {
            this.heroBehavior.destroy();
            this.heroBehavior = null;
        }
        if (this.heroSprite) {
            this.heroSprite.destroy();
            this.heroSprite = null;
        }
        if (this.heroHeldBlockSprite) {
            this.heroHeldBlockSprite.destroy();
            this.heroHeldBlockSprite = null;
        }

        // Clear portal indicator
        if (this.portalIndicator) {
            this.portalIndicator.destroy();
            this.portalIndicator = null;
        }

        // Reset local player (will be reinitialized by onInitialState)
        // Save current session ID so onInitialState can filter out our stale
        // session if the server hasn't processed our disconnect yet.
        // Only save for real online sessions (not 'local'). During the
        // Online(A) → Offline → Online(B) flow, the stale ID "A" must survive
        // through the intermediate offline step. Offline sessions use ID 'local'
        // which would never match a real server session.
        if (this.localPlayerId && this.localPlayerId !== 'local') {
            this.staleSessionId = this.localPlayerId;
        }
        this.localPlayer = null;
        this.localPlayerId = null;
    }

    private showPortalOverlay(): void {
        if (this.portalOverlay) return;
        const w = this.cameras.main.width;
        const h = this.cameras.main.height;
        // Portal overlay: tile the portal sprite across the screen.
        // Extract from atlas to standalone canvas — TileSprite cannot use atlas frames.
        if (!this.textures.exists('portal_tile')) {
            if (this.atlasFrames.has('portal')) {
                const canvas = this.textureManager.extractAtlasFrame('portal');
                if (canvas) this.textures.addCanvas('portal_tile', canvas);
            }
        }
        if (this.textures.exists('portal_tile')) {
            this.portalOverlay = this.add.tileSprite(0, 0, w, h, 'portal_tile');
            this.portalOverlay.setTint(0x555555);
        } else {
            // Fallback: solid purple rectangle if portal texture unavailable
            this.portalOverlay = this.add.tileSprite(0, 0, w, h, '__DEFAULT');
            this.portalOverlay.setTint(0x6600aa);
            this.portalOverlay.setAlpha(0.7);
        }
        this.portalOverlay.setOrigin(0, 0);
        this.portalOverlay.setDepth(999);
        this.portalOverlay.setScrollFactor(0);

        if (this.localPlayer) {
            const cam = this.cameras.main;
            const lp = this.localPlayer;
            // Match the exact offset formula from render()
            const screenCenterX = cam.width / 2;
            const screenCenterY = cam.height / 2;
            const playerScreenX = screenCenterX - TILE_SIZE / 2;
            const playerScreenY = screenCenterY - TILE_SIZE;
            const offsetX = playerScreenX - lp.x * TILE_SIZE;
            const offsetY = playerScreenY - lp.y * TILE_SIZE;

            // Draw room boxes (black background) on top of the portal overlay
            // Only the 2 main mentor house rooms (lower + upper) — not the
            // ladder shaft or exploration house.
            const portalRooms = this.roomBoxes.slice(0, 2);
            if (portalRooms.length > 0) {
                this.portalOverlayRoomBoxes = this.add.graphics();
                this.portalOverlayRoomBoxes.setDepth(1000);
                this.portalOverlayRoomBoxes.setScrollFactor(0);
                this.portalOverlayRoomBoxes.fillStyle(0x000000, 1);

                for (const box of portalRooms) {
                    const sx = box.x1 * TILE_SIZE + offsetX;
                    const sy = box.y1 * TILE_SIZE + offsetY;
                    const sw = (box.x2 - box.x1 + 1) * TILE_SIZE;
                    const sh = (box.y2 - box.y1 + 1) * TILE_SIZE;
                    this.portalOverlayRoomBoxes.fillRect(sx, sy, sw, sh);
                }

                // Underground darkness
                const undergroundScreenY = UNDERGROUND_LEVEL * TILE_SIZE + offsetY;
                if (undergroundScreenY < h) {
                    const clipY = Math.max(0, undergroundScreenY);
                    this.portalOverlayRoomBoxes.fillRect(0, clipY, w, h - clipY);
                }

                // Render room tile contents on top of the black boxes
                for (const box of portalRooms) {
                    for (let wy = box.y1; wy <= box.y2; wy++) {
                        for (let wx = box.x1; wx <= box.x2; wx++) {
                            const cx = Math.floor(wx / CHUNK_SIZE);
                            const cy = Math.floor(wy / CHUNK_SIZE);
                            const chunk = this.chunks.get(`${cx},${cy}`);
                            if (!chunk) continue;
                            const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                            const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                            const tile = chunk[ly]?.[lx] ?? TileType.SKY;
                            if (tile === TileType.SKY) continue;
                            const textureName = this.getTileTexture(tile as TileType);
                            const sx = wx * TILE_SIZE + offsetX;
                            const sy = wy * TILE_SIZE + offsetY;
                            const img = this.createImage(sx, sy, textureName);
                            img.setOrigin(0, 0);
                            img.setDepth(1001);
                            img.setScrollFactor(0);
                            this.portalOverlayTiles.push(img);
                        }
                    }
                }
            }

            // Snapshot the player sprite above the overlay
            const playerTextureKey = this.localPlayerSprite.texture.key;
            const playerFrameName = this.localPlayerSprite.frame.name;
            const px = lp.isCrouching ? playerScreenX : playerScreenX;
            const py = lp.isCrouching ? playerScreenY - TILE_SIZE : playerScreenY;
            this.portalOverlayPlayer = this.add.image(px, py, playerTextureKey, playerFrameName);
            this.portalOverlayPlayer.setOrigin(0, 0);
            this.portalOverlayPlayer.setDepth(1002);
            this.portalOverlayPlayer.setScrollFactor(0);
            if (this.localPlayerSprite.flipX) this.portalOverlayPlayer.setFlipX(true);
        }
    }

    private hidePortalOverlay(): void {
        if (this.portalOverlayPlayer) {
            this.portalOverlayPlayer.destroy();
            this.portalOverlayPlayer = null;
        }
        for (const img of this.portalOverlayTiles) img.destroy();
        this.portalOverlayTiles = [];
        if (this.portalOverlayRoomBoxes) {
            this.portalOverlayRoomBoxes.destroy();
            this.portalOverlayRoomBoxes = null;
        }
        if (!this.portalOverlay) return;
        this.portalOverlay.destroy();
        this.portalOverlay = null;
    }

    update(time: number, delta: number): void {
        const now = Date.now();

        // During death animation, freeze player input/movement but keep
        // slashes ticking so they expire naturally instead of persisting.
        if (this.deathAnim.isActive) {
            this.deathAnim.update(now);
            this.updateSlashes(now);
            this.render(now);
            return;
        }

        this.updateInput();

        // Don't run physics until spawn chunk is loaded (prevents falling through unloaded terrain)
        if (this.localPlayer && this.isPlayerChunkLoaded()) {
            this.updateLocalPlayer(now);
            // Detect first entry into mentor's house (room box 0)
            if (!this.hasVisitedMentorHouse && this.roomBoxes.length > 0) {
                const box = this.roomBoxes[0];
                const px = this.localPlayer.x;
                const py = this.localPlayer.y;
                if (px >= box.x1 && px <= box.x2 && py >= box.y1 && py <= box.y2) {
                    this.hasVisitedMentorHouse = true;
                }
            }
        }

        this.updateSlashes(now);
        this.updateMice(now);
        this.updateMentor(now);
        this.updateLover(now);
        this.updateHero(now);
        this.sendPositionUpdate(now);
        if (this.localPlayer) {
            this.chunkManager.updateChunkSubscriptions(this.localPlayer.x, this.localPlayer.y);
        }
        this.combatHUD.updateLoadIndicator(time);
        this.render(now);
    }

    /**
     * Check if the chunk containing the local player is loaded
     */
    private isPlayerChunkLoaded(): boolean {
        if (!this.localPlayer) return false;
        const cx = Math.floor(this.localPlayer.x / CHUNK_SIZE);
        const cy = Math.floor(this.localPlayer.y / CHUNK_SIZE);
        return this.chunks.has(`${cx},${cy}`);
    }

    // ============================================================
    // INPUT
    // ============================================================

    private updateInput(): void {
        const touch = this.touchControls.state;

        // T key or touch T button — toggle text input or send message
        // (must read talkJustPressed before clearJustPressed resets it)
        if (Phaser.Input.Keyboard.JustDown(this.tKey) || touch.talkJustPressed) {
            if (this.textInputOpen) {
                const textScene = this.scene.get('TextInputScene') as TextInputScene;
                if (textScene) textScene.handleTalkButton();
            } else {
                this.openTextInput();
            }
        }

        // Mobile touch buttons dismiss start menu and death screen
        if (touch.anyJustPressed) {
            if (this.startMenuOpen) {
                (this.scene.get('StartMenuScene') as StartMenuScene)?.dismiss();
            }
            if (this.deathScreenOpen) {
                (this.scene.get('DeathScreenScene') as DeathScreenScene)?.dismiss();
            }
        }

        // P key — toggle palette overlay
        if (Phaser.Input.Keyboard.JustDown(this.pKey)) {
            if (!this.paletteOpen && !this.textInputOpen && !this.deathScreenOpen && !this.deathAnim.isActive) {
                this.launchPaletteScene();
            }
        }

        this.touchControls.clearJustPressed();

        // While text input, start menu, death screen, or palette is open, suppress all game input but keep world ticking
        if (this.textInputOpen || this.startMenuOpen || this.deathScreenOpen || this.deathAnim.isActive || this.paletteOpen) {
            this.inputState.left = false;
            this.inputState.right = false;
            this.inputState.up = false;
            this.inputState.down = false;
            this.inputState.attack = false;
            this.inputState.downJustPressed = false;
            this.inputState.attackJustPressed = false;
            this.horizontalJustPressed = false;
            this.pendingLadderClimb = false;
            this.pendingLadderDescend = false;
            this.pendingLadderHorizontalDir = 0;
            this.wasClimbingLadder = false;
            this.lastDownState = false;
            this.lastUpState = false;
            this.lastLeftState = false;
            this.lastRightState = false;
            this.lastAttackState = false;
            return;
        }

        const currentDown = this.cursors.down.isDown || this.sKey.isDown || touch.down;
        const currentUp = this.cursors.up.isDown || this.wKey.isDown || touch.up;
        const currentLeft = this.cursors.left.isDown || this.aKey.isDown || touch.left;
        const currentRight = this.cursors.right.isDown || this.dKey.isDown || touch.right;
        const currentAttack = this.spaceKey.isDown || touch.attack;

        this.inputState.downJustPressed = (currentDown && !this.lastDownState) || touch.downJustPressed;
        this.inputState.attackJustPressed = (currentAttack && !this.lastAttackState) || touch.attackJustPressed;

        // Track horizontal just pressed
        const leftJustPressed = currentLeft && !this.lastLeftState;
        const rightJustPressed = currentRight && !this.lastRightState;
        this.horizontalJustPressed = leftJustPressed || rightJustPressed;

        // Track vertical just pressed (for ladder climb/descend buffering)
        const upJustPressed = currentUp && !this.lastUpState;
        if (upJustPressed) this.pendingLadderClimb = true;
        if (this.inputState.downJustPressed) this.pendingLadderDescend = true;

        this.lastDownState = currentDown;
        this.lastUpState = currentUp;
        this.lastLeftState = currentLeft;
        this.lastRightState = currentRight;
        this.lastAttackState = currentAttack;

        this.inputState.left = currentLeft;
        this.inputState.right = currentRight;
        this.inputState.up = currentUp;
        this.inputState.down = currentDown;
        this.inputState.attack = currentAttack;
    }

    /**
     * Open the toki pona text input overlay
     */
    private openTextInput(): void {
        if (this.textInputOpen || this.deathScreenOpen || this.deathAnim.isActive) return;

        // Ensure audio is loaded (usually already loading from create())
        if (!this.tokiAudio.loaded) {
            this.tokiAudio.load().catch(() => {});
        }

        // Suppress input but keep world ticking (no scene.pause)
        this.textInputOpen = true;
        this.scene.launch('TextInputScene', {
            selectedVoice: this.selectedVoiceKey,
            audio: this.tokiAudio,
            touchControls: this.touchControls,
            onMessageSend: (words: number[], voice: string) => {
                this.selectedVoiceKey = voice;
                this.networkClient.sendSpeech(words, voice);
            },
            onClose: () => {
                this.textInputOpen = false;
            },
        });
    }

    // ============================================================
    // SYNCHRONIZED MOVEMENT SYSTEM
    // ============================================================

    private updateLocalPlayer(now: number): void {
        updateLocalPlayer(this as any, now);
    }

    private processBlockInput(player: LocalPlayerState): void {
        processBlockInput(this as any, player as any);
    }

    private processCombatInput(player: LocalPlayerState, now: number): void {
        processCombatInput(this as any, player as any, now);
    }

    private processMovementInput(player: LocalPlayerState, now: number): boolean {
        return processMovementInput(this as any, player as any, now);
    }

    private processJumpAndFall(player: LocalPlayerState, now: number): boolean {
        return processJumpAndFall(this as any, player as any, now);
    }

    private processGlobalTick(player: LocalPlayerState, now: number, globalTick: number, isTickReady: boolean): void {
        processGlobalTick(this as any, player as any, now, globalTick, isTickReady);
    }

    private processContinuousCombatInput(player: LocalPlayerState, now: number): void {
        processContinuousCombatInput(this as any, player as any, now);
    }

    private doVerticalMove(player: LocalPlayerState, now: number): void {
        doVerticalMove(this as any, player as any, now);
    }

    private checkFallOffLedge(player: LocalPlayerState): void {
        checkFallOffLedge(this as any, player as any);
    }

    private handleDownAction(player: LocalPlayerState): void {
        handleDownAction(this as any, player as any);
    }

    // Execute the actual down action (place/pickup/crouch)
    // Called directly when grounded or for immediate actions
    // If useStoredPos is true, uses pendingActionX/Y instead of calculating from current position
    private executeDownAction(player: LocalPlayerState, useStoredPos: boolean = false): void {
        executeDownAction(this as any, player as any, useStoredPos);
    }

    private setLocalTile(x: number, y: number, type: TileType): void {
        const cx = Math.floor(x / CHUNK_SIZE);
        const cy = Math.floor(y / CHUNK_SIZE);
        const key = `${cx},${cy}`;
        const chunk = this.chunks.get(key);
        if (chunk) {
            const localX = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localY = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            chunk[localY][localX] = type;
        }
    }

    // Client-side falling block prediction
    // Called on same global tick as player fall to keep them synced
    private tickLocalFallingBlocks(): void {
        this.fallingBlocks.tickLocalFallingBlocks();
    }

    // ============================================================
    // PHYSICS HELPERS
    // ============================================================

    private isOnGround(player: LocalPlayerState): boolean {
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        if (!isPassable(this.getTile(player.x, player.y + h))) return true;
        // Also grounded if body overlaps a ladder
        return this.isInLadder(player);
    }

    private tryMoveHorizontal(player: LocalPlayerState, dir: number): boolean {
        const newX = player.x + dir;
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const feetY = player.y + h - 1;

        // Check if body space is clear at destination
        let canFitFlat = true;
        for (let y = 0; y < h; y++) {
            if (!isBodyPassable(this.getTile(newX, player.y + y))) {
                canFitFlat = false;
                break;
            }
        }

        if (canFitFlat) {
            // Body fits at same Y level - but check ground situation
            const hasGroundFlat = !isPassable(this.getTile(newX, player.y + h));
            const hasGroundStepDown = !isPassable(this.getTile(newX, player.y + h + 1));
            const canFitStepDown = isBodyPassable(this.getTile(newX, player.y + h)); // Space for body at y+1

            // Check if destination body overlaps a ladder (ladder provides support)
            let destHasLadder = false;
            for (let y = 0; y < h; y++) {
                if (isLadder(this.getTile(newX, player.y + y))) {
                    destHasLadder = true;
                    break;
                }
            }

            // Check if player is in a grounded STATE (not just physically on ground)
            // RISING/FALLING are "in air" even if momentarily touching ground
            const inGroundedState = player.state === PlayerState.IDLE ||
                                    player.state === PlayerState.WALKING ||
                                    player.state === PlayerState.CROUCHING;
            const currentlyGrounded = inGroundedState && this.isOnGround(player);

            if (hasGroundFlat || destHasLadder) {
                // Ground at same level or ladder support - flat move
                player.x = newX;
                return true;
            } else if (currentlyGrounded && canFitStepDown && hasGroundStepDown) {
                // No ground at same level, but ground at step-down - do diagonal step-down
                player.x = newX;
                player.y = player.y + 1;
                return true;
            } else if (!currentlyGrounded) {
                // In air state (RISING/FALLING) or not on ground - flat move is fine
                player.x = newX;
                return true;
            } else {
                // Walk-off-ledge: grounded state but no ground at destination
                // Return false - caller should handle via global fall tick
                return false;
            }
        }

        // Can't fit flat - try step up
        if (!isBodyPassable(this.getTile(newX, feetY))) {
            let canStep = true;
            const newY = player.y - 1;
            for (let y = 0; y < h; y++) {
                if (!isBodyPassable(this.getTile(newX, newY + y))) {
                    canStep = false;
                    break;
                }
            }
            if (canStep) {
                player.x = newX;
                player.y = newY;
                return true;
            }
        }

        return false;
    }

    private tryMoveUp(player: LocalPlayerState): boolean {
        if (!isBodyPassable(this.getTile(player.x, player.y - 1))) return false;
        player.y--;
        return true;
    }

    private tryMoveDown(player: LocalPlayerState): boolean {
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        if (!isPassable(this.getTile(player.x, player.y + h))) return false;
        player.y++;
        return true;
    }

    // Check if moving in direction would result in walk-off-ledge
    // (body fits but no ground at destination or step-down)
    // Only applies to grounded states - RISING/FALLING handle ledges differently
    private isWalkOffLedge(player: LocalPlayerState, dir: number): boolean {
        // Only check for grounded states - RISING and FALLING don't use this
        if (player.state !== PlayerState.IDLE &&
            player.state !== PlayerState.WALKING &&
            player.state !== PlayerState.CROUCHING) {
            return false;
        }

        if (!this.isOnGround(player)) return false; // Already not grounded

        const newX = player.x + dir;
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;

        // Check if body space is clear at destination
        for (let y = 0; y < h; y++) {
            if (!isBodyPassable(this.getTile(newX, player.y + y))) {
                return false; // Blocked, not walk-off-ledge
            }
        }

        // Body fits - check ground
        const hasGroundFlat = !isPassable(this.getTile(newX, player.y + h));
        const hasGroundStepDown = !isPassable(this.getTile(newX, player.y + h + 1));

        // Check if destination body overlaps a ladder (ladder provides support)
        for (let y = 0; y < h; y++) {
            if (isLadder(this.getTile(newX, player.y + y))) return false;
        }

        // Walk-off-ledge if no ground at flat OR step-down
        return !hasGroundFlat && !hasGroundStepDown;
    }

    /**
     * Check if any tile of the player's body overlaps a LADDER tile.
     */
    private isInLadder(player: LocalPlayerState): boolean {
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        for (let y = 0; y < h; y++) {
            if (isLadder(this.getTile(player.x, player.y + y))) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if the player is in a ladder OR standing on top of one.
     * Used to suppress crouch and enable descent from the top.
     */
    private isOnOrInLadder(player: LocalPlayerState): boolean {
        if (this.isInLadder(player)) return true;
        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        return isLadder(this.getTile(player.x, player.y + h));
    }

    private tryEndCrouch(player: LocalPlayerState): boolean {
        if (!player.isCrouching) return false;
        if (isBodyPassable(this.getTile(player.x, player.y - 1))) {
            player.y--;
            player.isCrouching = false;
            player.state = this.isOnGround(player) ? PlayerState.IDLE : PlayerState.FALLING;
            return true;
        }
        return false;
    }

    private getTile(x: number, y: number): TileType {
        const cx = Math.floor(x / CHUNK_SIZE);
        const cy = Math.floor(y / CHUNK_SIZE);
        const chunk = this.chunks.get(`${cx},${cy}`);
        if (!chunk) return TileType.SKY;
        const lx = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        const ly = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        return chunk[ly]?.[lx] ?? TileType.SKY;
    }

    // ============================================================
    // COMBAT
    // ============================================================

    /**
     * Client-side prediction for sword tile effects.
     * Mirrors server's checkSwordTile() and tickCombat sword section.
     * Gives instant feedback when the sword overlaps destructible tiles.
     */
    private predictSwordTile(player: LocalPlayerState, currentTick: number): void {
        this.combatRenderer.predictSwordTile(player, currentTick);
    }

    // Spawn a local slash prediction (called after movement if was walking)
    private spawnLocalSlash(player: LocalPlayerState, now: number): void {
        this.combatRenderer.spawnLocalSlash(player, now);
    }

    private updateSlashes(now: number): void {
        this.combatRenderer.updateSlashes(now);
    }

    // ============================================================
    // MOUSE ENEMIES (client-side prediction)
    // ============================================================

    private updateMice(now: number): void {
        const globalTick = Math.floor(now / TICK_INTERVAL);
        if (globalTick > this.mouseLastTick) {
            this.mouseLastTick = globalTick;
            const getTile = (x: number, y: number) => this.getTile(x, y);

            // Suffocation (block landed on mouse) — clean up sprites for killed mice
            const suffocated = this.clientMouseSim.tickSuffocation(getTile);
            for (const evt of suffocated) {
                if (evt.type === 'mouseDied') {
                    const sprite = this.mouseSprites.get(evt.mouseId);
                    if (sprite) {
                        sprite.destroy();
                        this.mouseSprites.delete(evt.mouseId);
                    }
                }
            }

            // Movement (grounded only) + falling (same as server)
            const absorptions = this.clientMouseSim.tickMovement(getTile);
            for (const abs of absorptions) {
                // Remove absorbed mouse sprite
                const sprite = this.mouseSprites.get(abs.mouseId);
                if (sprite) {
                    sprite.destroy();
                    this.mouseSprites.delete(abs.mouseId);
                }
            }
            this.clientMouseSim.tickFalling(getTile);
            // No combat - that's server-authoritative
        }
    }

    private updateMentor(now: number): void {
        if (!this.mentorBehavior || !this.localPlayer) return;
        const { offsetX, offsetY } = this.getCameraOffset();
        this.mentorBehavior.update(
            now,
            this.localPlayer.x,
            this.localPlayer.y,
            this.localPlayer.isHoldingBlock,
            this.localPlayer.heldBlockType ?? -1,
            this.roomBoxes,
            offsetX,
            offsetY,
        );
    }

    private updateLover(now: number): void {
        if (!this.loverBehavior || !this.localPlayer) return;
        const { offsetX, offsetY } = this.getCameraOffset();

        // Build map of other player positions for multi-player awareness
        const otherPositions = new Map<string, { x: number; y: number }>();
        for (const [id, queue] of this.otherPlayers) {
            const pos = queue.getCurrentPosition();
            if (pos) {
                otherPositions.set(id, { x: pos.x, y: pos.y });
            }
        }

        this.loverBehavior.update(
            now,
            this.localPlayer.x,
            this.localPlayer.y,
            this.roomBoxes,
            otherPositions,
            offsetX,
            offsetY,
        );

        // Auto-face toward Lover after 300ms stationary in her room
        const facingOverride = this.loverBehavior.getPlayerFacingOverride(
            this.localPlayer.x, this.localPlayer.y, now, this.roomBoxes,
        );
        if (facingOverride !== null) {
            this.localPlayer.facing = facingOverride;
        }
    }

    private updateHero(now: number): void {
        if (!this.heroBehavior) return;
        // Feed player position so hero can wait for player during escape
        if (this.localPlayer) {
            this.heroBehavior.playerX = this.localPlayer.x;
        }
        this.heroBehavior.update(now);
    }

    // ============================================================
    // NETWORK
    // ============================================================

    private sendPositionUpdate(now: number): void {
        if (!this.localPlayer) return;
        if (now - this.lastPositionSendTime < 33) return;
        this.lastPositionSendTime = now;

        const p = this.localPlayer;
        this.networkClient.sendPosition(
            p.x, p.y, p.state, p.facing,
            p.isCrouching, p.isHoldingBlock, p.heldBlockType
        );
    }

    // ============================================================
    // RENDERING
    // ============================================================

    private getCameraOffset(): { offsetX: number; offsetY: number } {
        if (!this.localPlayer) return { offsetX: 0, offsetY: 0 };
        const screenCenterX = this.cameras.main.width / 2;
        const screenCenterY = this.cameras.main.height / 2;
        const playerScreenX = screenCenterX - TILE_SIZE / 2;
        const playerScreenY = screenCenterY - TILE_SIZE;
        return {
            offsetX: playerScreenX - this.localPlayer.x * TILE_SIZE,
            offsetY: playerScreenY - this.localPlayer.y * TILE_SIZE,
        };
    }

    private render(now: number): void {
        renderSceneFrame(this as any, now);
    }

    private showHurtBubble(playerId: string): void {
        const { offsetX, offsetY } = this.getCameraOffset();
        const isLocal = playerId === this.localPlayerId;
        const words = [68]; // "o" glyph index (row 5, col 8 in 12-column spritesheet)
        const voiceKey = isLocal ? this.selectedVoiceKey : 'pona';
        const profile = VOICE_PROFILES[voiceKey] || undefined;

        if (isLocal && this.localPlayer) {
            if (this.localSpeechBubble) {
                this.localSpeechBubble.destroy();
            }
            const screenX = this.localPlayer.x * TILE_SIZE + offsetX;
            const screenY = this.localPlayer.y * TILE_SIZE + offsetY;
            this.localSpeechBubble = new SpeechBubble({
                scene: this,
                words,
                playerScreenX: screenX,
                playerScreenY: screenY,
                audio: this.tokiAudio,
                profile,
            });
        } else {
            const queue = this.otherPlayers.get(playerId);
            const pos = queue?.getCurrentPosition();
            if (pos) {
                const existingBubble = this.otherPlayerSpeechBubbles.get(playerId);
                if (existingBubble) {
                    existingBubble.destroy();
                }
                const screenX = pos.x * TILE_SIZE + offsetX;
                const screenY = pos.y * TILE_SIZE + offsetY;
                const bubble = new SpeechBubble({
                    scene: this,
                    words,
                    playerScreenX: screenX,
                    playerScreenY: screenY,
                    audio: this.tokiAudio,
                    profile,
                });
                this.otherPlayerSpeechBubbles.set(playerId, bubble);
            }
        }
    }

    private updatePlayerSprite(
        sprite: Phaser.GameObjects.Sprite,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        playerTextures?: PlayerTextures,
        isHurt: boolean = false,
        hasBoots: boolean = false,
    ): void {
        // Use player-specific textures if provided, otherwise use default
        const textures = playerTextures || this.processedTextures;

        // Hurt sprite overrides all other states
        if (isHurt) {
            this.applyTexture(sprite, hasBoots ? textures.man_hurt_booted_right : textures.man_hurt_right);
            sprite.setFlipX(facing === -1);
            return;
        }

        // Choose texture based on state, using booted variants when equipped
        let texture: string;

        if (isCrouching || state === PlayerState.JUMPSQUAT || state === PlayerState.CROUCHING) {
            texture = hasBoots ? textures.squat_man_booted_right : textures.squat_man_right;
        } else if (state === PlayerState.RISING) {
            texture = hasBoots ? textures.leap_man_booted_right : textures.leap_man_right;
        } else if (state === PlayerState.FALLING) {
            texture = hasBoots ? textures.fall_man_booted_right : textures.fall_man_right;
        } else {
            texture = hasBoots ? textures.man_booted_right : textures.man_right;
        }

        this.applyTexture(sprite, texture);

        // Flip for left facing
        sprite.setFlipX(facing === -1);
    }

    // Get the processed texture key for a tile type
    private getTileTexture(tileType: TileType): string {
        switch (tileType) {
            case TileType.DIRT:
                return this.processedTextures.dirt;
            case TileType.GRASS:
                return this.processedTextures.grass;
            case TileType.RUNE:
                return 'rune_generated';
            case TileType.PORTAL:
                return this.processedTextures.portal;
            case TileType.SKULL:
                return this.processedTextures.skull;
            case TileType.HEART_CONTAINER:
                return 'heart_container';
            case TileType.BIG_HEART_CONTAINER:
                return 'big_heart_container';
            case TileType.LADDER:
                return this.processedTextures.ladder;
            case TileType.STICK:
                return this.processedTextures.stick;
            case TileType.LEAVES:
                return this.processedTextures.leaves;
            case TileType.TRUNK:
                return this.processedTextures.trunk;
            case TileType.LOG:
                return this.processedTextures.log;
            case TileType.FLINT:
                return this.processedTextures.flint;
            case TileType.PLANK:
                return this.processedTextures.plank;
            case TileType.AMETHYST:
                return this.processedTextures.amethyst;
            case TileType.OPAL:
                return this.processedTextures.opal;
            case TileType.TURQUOISE:
                return this.processedTextures.turquoise;
            case TileType.SAPLING:
                return 'sapling_generated';
            case TileType.RATS_NEST:
                return 'rats_nest_generated';
            case TileType.WATER:
            case TileType.WATER_SURFACE:
                return 'water_generated';
            case TileType.WOODEN_SHIELD:
                return this.processedTextures.wooden_shield;
            case TileType.STONE_CLUB:
                return this.processedTextures.stone_club;
            case TileType.IRON_SWORD:
                return this.processedTextures.iron_sword;
            case TileType.RED_STONE:
                return this.processedTextures.red_stone;
            case TileType.BOOTS:
                return this.processedTextures.boots;
            case TileType.YELLOW_DOOR:
                return this.processedTextures.yellow_door_bottom;
            case TileType.YELLOW_DOOR_OPEN:
                return this.processedTextures.yellow_door_open_bottom;
            case TileType.YELLOW_KEY:
                return this.processedTextures.yellow_key;
            case TileType.STONE:
                return this.processedTextures.stone;
            case TileType.ILO_ANU:
                return 'dice_face_1'; // Default face; actual rendering uses worldData face lookup
            case TileType.KIWEN_ANPA:
                return this.processedTextures.kiwen_anpa;
            case TileType.KIWEN_NIMI:
                return this.processedTextures.kiwen_nimi;
            case TileType.KIWEN_SEME:
                return this.processedTextures.kiwen_seme;
            case TileType.KIWEN_LUKIN:
                return this.processedTextures.kiwen_lukin;
            case TileType.KIWEN_NOKA:
                return this.processedTextures.kiwen_noka;
            case TileType.KIWEN_LA:
                return this.processedTextures.kiwen_la;
            case TileType.KIWEN_SUPA:
                return this.processedTextures.kiwen_supa;
            case TileType.KIWEN_TOKI:
                return this.processedTextures.kiwen_toki;
            case TileType.KIWEN_MI:
                return this.processedTextures.kiwen_mi;
            case TileType.KIWEN_SOWELI:
                return this.processedTextures.kiwen_soweli;
            case TileType.KIWEN_IJO:
                return this.processedTextures.kiwen_ijo;
            case TileType.KIWEN_JAN:
                return this.processedTextures.kiwen_jan;
            case TileType.KIWEN_JELO:
                return this.processedTextures.kiwen_jelo;
            case TileType.KIWEN_JO:
                return this.processedTextures.kiwen_jo;
            case TileType.KIWEN_KALA:
                return this.processedTextures.kiwen_kala;
            case TileType.KIWEN_KALAMA:
                return this.processedTextures.kiwen_kalama;
            case TileType.KIWEN_KAMA:
                return this.processedTextures.kiwen_kama;
            case TileType.KIWEN_KO:
                return this.processedTextures.kiwen_ko;
            case TileType.KIWEN_KULUPU:
                return this.processedTextures.kiwen_kulupu;
            case TileType.KIWEN_KUTE:
                return this.processedTextures.kiwen_kute;
            case TileType.KIWEN_LEN:
                return this.processedTextures.kiwen_len;
            case TileType.KIWEN_LETE:
                return this.processedTextures.kiwen_lete;
            case TileType.KIWEN_LI:
                return this.processedTextures.kiwen_li;
            case TileType.KIWEN_LIPU:
                return this.processedTextures.kiwen_lipu;
            case TileType.KIWEN_LUKA:
                return this.processedTextures.kiwen_luka;
            case TileType.KIWEN_MAMA:
                return this.processedTextures.kiwen_mama;
            case TileType.KIWEN_MANI:
                return this.processedTextures.kiwen_mani;
            case TileType.KIWEN_MOKU:
                return this.processedTextures.kiwen_moku;
            case TileType.KIWEN_MOLI:
                return this.processedTextures.kiwen_moli;
            case TileType.KIWEN_NASIN:
                return this.processedTextures.kiwen_nasin;
            case TileType.KIWEN_NI:
                return this.processedTextures.kiwen_ni;
            case TileType.KIWEN_O:
                return this.processedTextures.kiwen_o;
            case TileType.KIWEN_OLIN:
                return this.processedTextures.kiwen_olin;
            case TileType.KIWEN_ONA:
                return this.processedTextures.kiwen_ona;
            case TileType.KIWEN_PAKALA:
                return this.processedTextures.kiwen_pakala;
            case TileType.KIWEN_PAN:
                return this.processedTextures.kiwen_pan;
            case TileType.KIWEN_PANA:
                return this.processedTextures.kiwen_pana;
            case TileType.KIWEN_PI:
                return this.processedTextures.kiwen_pi;
            case TileType.KIWEN_PIMEJA:
                return this.processedTextures.kiwen_pimeja;
            case TileType.KIWEN_PIPI:
                return this.processedTextures.kiwen_pipi;
            case TileType.KIWEN_POKI:
                return this.processedTextures.kiwen_poki;
            case TileType.KIWEN_PU:
                return this.processedTextures.kiwen_pu;
            case TileType.KIWEN_SELI:
                return this.processedTextures.kiwen_seli;
            case TileType.KIWEN_SIJELO:
                return this.processedTextures.kiwen_sijelo;
            case TileType.KIWEN_SUNO:
                return this.processedTextures.kiwen_suno;
            case TileType.KIWEN_TOMO:
                return this.processedTextures.kiwen_tomo;
            case TileType.KIWEN_UNPA:
                return this.processedTextures.kiwen_unpa;
            case TileType.KIWEN_WAWA:
                return this.processedTextures.kiwen_wawa;
            default: {
                // KASI tiles — use atlas frames directly
                const kasiTex = this.kasiProcessedTextures.get(tileType);
                if (kasiTex) return kasiTex;
                // Check for atlas frame with tile name
                const enumName = TileType[tileType]?.toLowerCase();
                if (enumName && this.atlasFrames.has(enumName)) return enumName;
                // Data-driven placeholder tiles
                const placeholderKey = `placeholder_${tileType}`;
                if (this.textures.exists(placeholderKey)) {
                    return placeholderKey;
                }
                return this.processedTextures.stone;
            }
        }
    }

    // Check if a tile type is affected by gravity (can fall)
    private isFallingType(tileType: TileType): boolean {
        return this.fallingBlocks.isFallingType(tileType);
    }

    private propagateLocalInstabilityUp(x: number, y: number): void {
        this.fallingBlocks.propagateLocalInstabilityUp(x, y);
    }

    /** Launch the palette overlay. Extracted so dictionary→back can reopen it. */
    private launchPaletteScene(): void {
        this.paletteOpen = true;
        this.scene.launch('PaletteScene', {
            playerTileX: this.localPlayer?.x,
            onClose: () => { this.paletteOpen = false; },
            onOpenDictionary: (wordA: number, wordB: number) => {
                this.paletteOpen = false;
                this.dictionaryOpen = true;
                this.scene.launch('DictionaryScene', {
                    initialA: wordA,
                    initialB: wordB,
                    onClose: () => {
                        this.dictionaryOpen = false;
                        this.launchPaletteScene();
                    },
                });
            },
        });
    }

}
